/**
 * AdGraphicsBuilder – full UI for generating AI graphic ads.
 * Prompt field, quantity selector, format/resolution, expandable sections
 * for product info, brand details, visual inspiration (with file upload),
 * and layout schema templates.
 */
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient, getAuthToken, uploadFile } from "@/lib/queryClient";
import { MAX_INSPIRATION_FILES, availableInspirationSlots } from "@/lib/inspiration";
import { looksLikeYtThumbnail } from "@/lib/yt-thumbnail";
import { ToastAction } from "@/components/ui/toast";
import { useJobPolling, type JobPollFailure } from "@/hooks/use-job-polling";
import { useStore } from "@/lib/store";
import { useBackgroundJobsStore } from "@/lib/background-jobs-store";
import { mergeGraphicsById } from "@/lib/merge-generated-graphics";
import { groupGraphicsByJob } from "@/lib/group-graphics-by-job";
import { MAX_CONCURRENT_AD_GRAPHIC_JOBS } from "@shared/generation-limits";
import { summarizeImagePrompt, resolveStructuredPrompt } from "@shared/image-prompt-summary";
import { useAuth } from "@/hooks/useAuth";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import {
  useAllUserMedia,
  CATEGORY_LABELS,
  SOURCE_LABELS,
  type UnifiedMediaItem,
} from "@/hooks/useUserMedia";
import { useToast } from "@/hooks/use-toast";
import { FriendlyErrorMapper } from "@/lib/errors/FriendlyErrorMapper";
import { parseServerErrorBody } from "@/lib/errors/server-error-body";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Download,
  RefreshCw,
  Image as ImageIcon,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Plus,
  Minus,
  X,
  ZoomIn,
  Upload,
  ShoppingBag,
  Brush,
  Eye,
  Shuffle,
  FileText,
  Wand2,
  Rocket,
  Check,
  Users,
  Target,
  Search,
  ChevronDown,
  TrendingUp,
  Trash2,
  ImagePlus,
  ThumbsUp,
  ThumbsDown,
  Link2,
  Youtube,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import BrandTemplateEditor from "@/components/brand-template-editor";
import { ProductPickerModal } from "@/components/ugc-studio/product-picker-modal";
import { AvatarPickerModal } from "@/components/ugc-studio/avatar-picker-modal";
import type { UgcAvatar, UgcProduct } from "@/components/ugc-studio/api";
import AvatarSelector from "@/components/avatar-selector";
import SavedPromptsPicker from "@/components/saved-prompts-picker";
import { useAvatars } from "@/hooks/useAvatars";
import { useRecentPrompts } from "@/hooks/useRecentPrompts";
import { SectionInfoBubble } from "@/components/ui/section-info-bubble";
import {
  useProgressIllusion,
  PsychologicalProgressBar,
} from "@/components/ui/psychological-progress-bar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ImageModelPicker, type ImageModelOption } from "@/components/image-model-picker";
import { stripImageMetadata } from "@/lib/strip-image-metadata";

// Brand book PDF upload was deprecated 2026-05 in favor of Avatar context.
// Toggle to true to restore it without re-deploying.
const SHOW_BRAND_BOOK_UPLOAD = false;

// ── Types ─────────────────────────────────────────────────

type AdFormat = "feed" | "story" | "square" | "landscape" | "banner";
type GraphicStyle =
  | "minimalist"
  | "lifestyle"
  | "bold-neon"
  | "corporate"
  | "illustration"
  | "3d-render"
  | "gradient-abstract";
type GenerationMode = "auto" | "generate" | "edit";

interface GeneratedGraphic {
  id: string;
  format: AdFormat;
  variantIndex: number;
  imageUrl: string;
  prompt: string;
  size: string;
  imageModel?: string;
}

// ── VP-206: tracker pojedynczego joba generowania ──────────────────────────
// Hooki są per-instancja komponentu, więc przy N równoległych jobach renderujemy
// N trackerów (bez UI) — każdy polluje swój job i raportuje snapshot rodzicowi.
type GenJobSnapshot = {
  status: string;
  graphics?: GeneratedGraphic[];
  resultUrls?: string[];
  progress?: number;
  errorMessage?: string | null;
  meta?: { tokensDeducted: number; tokensRemaining: number };
  /** Etykieta zadania (prompt) z DTO — odzyskiwana po refreshu/deep-linku. */
  promptLabel?: string;
};

function GraphicJobTracker({
  jobId,
  onSnapshot,
  onPollFailure,
}: {
  jobId: string;
  onSnapshot: (jobId: string, snapshot: GenJobSnapshot) => void;
  onPollFailure: (jobId: string, failure: JobPollFailure) => void;
}) {
  const { job, pollFailure } = useJobPolling<GenJobSnapshot>(jobId);
  useEffect(() => {
    if (job) onSnapshot(jobId, job);
  }, [job, jobId, onSnapshot]);
  useEffect(() => {
    if (pollFailure) onPollFailure(jobId, pollFailure);
  }, [pollFailure, jobId, onPollFailure]);
  return null;
}

const NO_ACTIVE_GEN_JOBS: string[] = [];

// Wiersz panelu kolejki z WŁASNĄ iluzją postępu: wspólna iluzja nie resetowała
// się między jobami (anyGenerating pozostaje true), więc wiersz nowego joba
// startował z ~90% i spadał do realnych 0-5%. Własny hook montuje się razem
// z wierszem = świeży start; pokazujemy max(realny, iluzja) — pasek nie cofa się.
function QueueRow({ label, realProgress }: { label: string; realProgress?: number }) {
  const illusion = useProgressIllusion({ active: true, tau: 10 });
  const pct = Math.max(realProgress ?? 0, Math.round(illusion * 100));
  return (
    <div>
      <div className="flex items-center gap-3 mb-1.5">
        <Loader2 className="w-4 h-4 animate-spin text-red-400" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-white truncate">{label}</p>
            <span className="text-[10px] text-white/30 tabular-nums ml-2 shrink-0">{pct}%</span>
          </div>
        </div>
      </div>
      <PsychologicalProgressBar progress={pct / 100} color="red" heightClass="h-1.5" shimmer />
    </div>
  );
}

interface BrandAsset {
  id: string;
  userId: string;
  name: string;
  description: string;
  fileUrl: string;
  fileType: string;
  mimeType: string;
  fileSizeBytes: number | null;
  storagePath: string;
  createdAt: string;
}

interface GenerateResult {
  graphics: GeneratedGraphic[];
  totalImages: number;
  meta: { tokensDeducted: number; tokensRemaining: number };
}

// ── Constants ─────────────────────────────────────────────

// previewW/previewH = px size of the proportional preview rectangle (max ~28px on long side).
const FORMATS: {
  id: AdFormat;
  label: string;
  size: string;
  icon: string;
  ratio: string;
  previewW: number;
  previewH: number;
}[] = [
  {
    id: "feed",
    label: "Feed",
    size: "1080×1350",
    icon: "📱",
    ratio: "4:5",
    previewW: 22,
    previewH: 28,
  },
  {
    id: "story",
    label: "Story",
    size: "1080×1920",
    icon: "📲",
    ratio: "9:16",
    previewW: 16,
    previewH: 28,
  },
  {
    id: "square",
    label: "Square",
    size: "1080×1080",
    icon: "⬜",
    ratio: "1:1",
    previewW: 26,
    previewH: 26,
  },
  {
    id: "landscape",
    label: "Landscape",
    size: "1920×1080",
    icon: "🖥️",
    ratio: "16:9",
    previewW: 32,
    previewH: 18,
  },
  {
    id: "banner",
    label: "Banner",
    size: "1200×628",
    icon: "🪧",
    ratio: "1.91:1",
    previewW: 36,
    previewH: 19,
  },
];

const STYLES: { id: GraphicStyle; label: string; emoji: string; desc: string }[] = [
  {
    id: "minimalist",
    label: "styles.minimalist.label",
    emoji: "⚪",
    desc: "styles.minimalist.desc",
  },
  { id: "lifestyle", label: "styles.lifestyle.label", emoji: "🌅", desc: "styles.lifestyle.desc" },
  { id: "bold-neon", label: "styles.boldNeon.label", emoji: "💜", desc: "styles.boldNeon.desc" },
  { id: "corporate", label: "styles.corporate.label", emoji: "🏢", desc: "styles.corporate.desc" },
  {
    id: "illustration",
    label: "styles.illustration.label",
    emoji: "🎨",
    desc: "styles.illustration.desc",
  },
  { id: "3d-render", label: "styles.3dRender.label", emoji: "🧊", desc: "styles.3dRender.desc" },
  {
    id: "gradient-abstract",
    label: "styles.gradientAbstract.label",
    emoji: "🌈",
    desc: "styles.gradientAbstract.desc",
  },
];

const QUANTITY_OPTIONS = [1, 2, 4, 10, 20];
const ORGANIC_QUANTITY_OPTIONS = [1, 2, 4];

const LAYOUT_TEMPLATES = [
  { id: "product-hero", label: "layouts.productHero.label", desc: "layouts.productHero.desc" },
  { id: "testimonial", label: "layouts.testimonial.label", desc: "layouts.testimonial.desc" },
  { id: "before-after", label: "layouts.beforeAfter.label", desc: "layouts.beforeAfter.desc" },
  { id: "sale-promo", label: "layouts.salePromo.label", desc: "layouts.salePromo.desc" },
  { id: "benefits-list", label: "layouts.benefitsList.label", desc: "layouts.benefitsList.desc" },
  { id: "lifestyle-use", label: "layouts.lifestyleUse.label", desc: "layouts.lifestyleUse.desc" },
  { id: "minimal-text", label: "layouts.minimalText.label", desc: "layouts.minimalText.desc" },
  { id: "comparison", label: "layouts.comparison.label", desc: "layouts.comparison.desc" },
  { id: "unboxing", label: "layouts.unboxing.label", desc: "layouts.unboxing.desc" },
  {
    id: "youtube-thumbnail",
    label: "layouts.youtubeThumbnail.label",
    desc: "layouts.youtubeThumbnail.desc",
  },
];

const CTA_PRESETS = [
  "Sprawdź teraz",
  "Zapisz się",
  "Kup teraz",
  "Dowiedz się więcej",
  "Umów się",
  "Odbierz za darmo",
  "Zarezerwuj miejsce",
  "Rozpocznij",
];

type FunnelType =
  | "quick-form"
  | "landing-form"
  | "webinar"
  | "vsl"
  | "ecommerce"
  | "consultation"
  | "free-trial"
  | "messenger"
  | "";

const FUNNEL_OPTIONS: { id: FunnelType; label: string; emoji: string }[] = [
  { id: "quick-form", label: "funnelOptions.quickForm", emoji: "⚡" },
  { id: "landing-form", label: "funnelOptions.landingForm", emoji: "📋" },
  { id: "webinar", label: "funnelOptions.webinar", emoji: "🎓" },
  { id: "vsl", label: "funnelOptions.vsl", emoji: "🎬" },
  { id: "ecommerce", label: "funnelOptions.ecommerce", emoji: "🛒" },
  { id: "consultation", label: "funnelOptions.consultation", emoji: "📞" },
  { id: "free-trial", label: "funnelOptions.freeTrial", emoji: "🎁" },
  { id: "messenger", label: "funnelOptions.messenger", emoji: "💬" },
];

const FUNNEL_HINT_KEYS: Partial<
  Record<FunnelType, { headline: string; primaryText: string; cta: string }>
> = {
  webinar: {
    headline: "funnelHints.webinar.headline",
    primaryText: "funnelHints.webinar.primaryText",
    cta: "funnelHints.webinar.cta",
  },
  "quick-form": {
    headline: "funnelHints.quickForm.headline",
    primaryText: "funnelHints.quickForm.primaryText",
    cta: "funnelHints.quickForm.cta",
  },
  "landing-form": {
    headline: "funnelHints.landingForm.headline",
    primaryText: "funnelHints.landingForm.primaryText",
    cta: "funnelHints.landingForm.cta",
  },
  consultation: {
    headline: "funnelHints.consultation.headline",
    primaryText: "funnelHints.consultation.primaryText",
    cta: "funnelHints.consultation.cta",
  },
  vsl: {
    headline: "funnelHints.vsl.headline",
    primaryText: "funnelHints.vsl.primaryText",
    cta: "funnelHints.vsl.cta",
  },
  ecommerce: {
    headline: "funnelHints.ecommerce.headline",
    primaryText: "funnelHints.ecommerce.primaryText",
    cta: "funnelHints.ecommerce.cta",
  },
};

const IMAGE_MODELS: ImageModelOption[] = [
  {
    id: "nano-banana-pro",
    label: "Nano Banana Pro",
    provider: "fal.ai",
    description:
      "Najlepszy do reklam lead-gen z du\u017c\u0105 ilo\u015bci\u0105 tekstu \u2014 wierne renderowanie napis\u00f3w, czyste layouty.",
    featured: true,
    glyph: "G",
    accent: "bg-[#ef3e29] text-white",
  },
  {
    id: "gpt-image-2",
    label: "GPT Image 2",
    provider: "fal.ai",
    description:
      "Reklamy 4K z długim tekstem (headline + body + CTA) — najlepiej zachowuje litery bez artefaktów.",
    badge: "NEW",
    featured: true,
    glyph: "G",
    accent: "bg-emerald-500/90 text-white",
  },
  {
    id: "seedream-5.0-lite",
    label: "Seedream 5.0 lite",
    provider: "fal.ai",
    description:
      "Szybkie warianty i prototypowanie — niski koszt, dobra jakość, mniej precyzji w detalach.",
    badge: "NEW",
    featured: true,
    glyph: "S",
    accent: "bg-sky-500/90 text-white",
  },
  {
    id: "seedream-4.5",
    label: "Seedream 4.5",
    provider: "fal.ai",
    description:
      "E-commerce / kompozycje z avatarem i produktem \u2014 najmocniejszy w zachowywaniu twarzy i kszta\u0142t\u00f3w referencji.",
    featured: true,
    glyph: "S",
    accent: "bg-indigo-500/90 text-white",
  },
  {
    id: "nano-banana-2",
    label: "Nano Banana 2",
    provider: "fal.ai",
    description:
      "Tańsza wersja Pro — gdy potrzebujesz dużo grafik szybko i nie zależy ci na detalach typu fotorealizm.",
    badge: "PREMIUM",
    featured: true,
    glyph: "G",
    accent: "bg-white/[0.08] text-white",
  },
];

const GENERATION_MODES: { id: GenerationMode; label: string; desc: string }[] = [
  { id: "auto", label: "Auto", desc: "generationModes.auto.desc" },
  { id: "generate", label: "Generate", desc: "generationModes.generate.desc" },
  { id: "edit", label: "Edit", desc: "generationModes.edit.desc" },
];

const ALL_MODEL_IDS = [
  "nano-banana-pro",
  "nano-banana-2",
  "gpt-image-2",
  "seedream-4.5",
  "seedream-5.0-lite",
  "seedream-4.0",
];

const IMAGE_RESOLUTIONS: {
  id: "0.5K" | "1K" | "2K" | "4K";
  label: string;
  costLabel: string;
  models: string[];
}[] = [
  { id: "0.5K", label: "0.5K", costLabel: "0.75x", models: ["nano-banana-2"] },
  { id: "1K", label: "1K", costLabel: "1x", models: ALL_MODEL_IDS },
  { id: "2K", label: "2K", costLabel: "1.5x", models: ALL_MODEL_IDS },
  { id: "4K", label: "4K", costLabel: "2x", models: ALL_MODEL_IDS },
];

// ── Example Templates ─────────────────────────────────────

interface ExampleTemplate {
  id: string;
  label: string;
  thumbnail: string;
  style?: GraphicStyle;
  layout?: string;
}

const EXAMPLE_TEMPLATES: ExampleTemplate[] = [
  {
    id: "client-ascension-cyberpunk",
    label: "Cyberpunk AI Ad",
    thumbnail:
      "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Image+Error.png",
    style: "bold-neon",
    layout: "product-hero",
  },
  {
    id: "lead-gen-portrait",
    label: "Lead Gen — portret",
    thumbnail:
      "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Zdj%C4%99cia+inspiracyjne+do+tw%C3%B3rcy+grafik+reklamowych/szukam+14+osob+kurs.png",
    style: "lifestyle",
    layout: "product-hero",
  },
  {
    id: "bieszczady-real-estate",
    label: "Nieruchomość — góry",
    thumbnail:
      "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Zdj%C4%99cia+inspiracyjne+do+tw%C3%B3rcy+grafik+reklamowych/dom+w+bieszczadach.png",
    style: "lifestyle",
    layout: "benefits-list",
  },
  {
    id: "darmowy-trening-ebook",
    label: "Darmowy trening",
    thumbnail:
      "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Zdj%C4%99cia+inspiracyjne+do+tw%C3%B3rcy+grafik+reklamowych/darmowy+trening+dowiedz+sie+wieej.png",
    style: "lifestyle",
    layout: "product-hero",
  },
  {
    id: "premium-portrait-cta",
    label: "Premium portret + CTA",
    thumbnail:
      "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Zdj%C4%99cia+inspiracyjne+do+tw%C3%B3rcy+grafik+reklamowych/po+prawej+osoba+z+cta.png",
    style: "lifestyle",
    layout: "product-hero",
  },
  {
    id: "typo-red-bold",
    label: "Typografia — bold red",
    thumbnail:
      "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Zdj%C4%99cia+inspiracyjne+do+tw%C3%B3rcy+grafik+reklamowych/brzydka+grafika+1.png",
    style: "bold-neon",
    layout: "product-hero",
  },
  {
    id: "pilates-pink-minimal",
    label: "Minimal — pastel pink",
    thumbnail:
      "https://s3.eu-north-1.amazonaws.com/viral.partners-776710289113-eu-north-1-an/Zdj%C4%99cia+inspiracyjne+do+tw%C3%B3rcy+grafik+reklamowych/Brzydka+grafika+2.png",
    style: "minimalist",
    layout: "product-hero",
  },
];

// ── Component ─────────────────────────────────────────────

interface InspirationContext {
  adName: string;
  primaryText: string;
  headline: string;
  imageUrl: string;
}

export interface AdGraphicsBuilderCampaignHandoff {
  adPrompt: string;
  referenceImages: { name: string; dataUrl: string }[];
  onGenerated?: (url: string) => void | Promise<void>;
  businessModel?: "ecommerce_direct" | "consultation_booking" | "lead_collection";
}

interface AdGraphicsBuilderProps {
  selectedModel?: string;
  initialPrompt?: string;
  initialLayout?: string;
  initialImageModel?: string;
  contentType?: "ad" | "organic";
  inspirationContext?: InspirationContext;
  campaignHandoff?: AdGraphicsBuilderCampaignHandoff;
}

// ── Rotating loading messages ─────────────────────────────
const COPY_LOADING_KEYS = [
  "loadingMessages.copy.brewing",
  "loadingMessages.copy.cooking",
  "loadingMessages.copy.roas",
  "loadingMessages.copy.cpm",
  "loadingMessages.copy.influencers",
  "loadingMessages.copy.algorithm",
  "loadingMessages.copy.viral",
  "loadingMessages.copy.copywriter",
  "loadingMessages.copy.testing",
  "loadingMessages.copy.analyzing",
];

const GENERATE_LOADING_KEYS = [
  "loadingMessages.generate.painting",
  "loadingMessages.generate.rendering",
  "loadingMessages.generate.gpu",
  "loadingMessages.generate.composing",
  "loadingMessages.generate.colors",
  "loadingMessages.generate.banners",
  "loadingMessages.generate.transforming",
  "loadingMessages.generate.asking",
];

function useRotatingMessage(messages: string[], intervalMs = 2200): string {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
    const id = setInterval(() => setIndex((i) => (i + 1) % messages.length), intervalMs);
    return () => clearInterval(id);
  }, [messages, intervalMs]);
  return messages[index];
}

function FormatPicker({
  selectedFormats,
  toggleFormat,
}: {
  selectedFormats: AdFormat[];
  toggleFormat: (f: AdFormat) => void;
}) {
  const primary = FORMATS.find((f) => selectedFormats.includes(f.id)) ?? FORMATS[0];
  const extraCount = Math.max(0, selectedFormats.length - 1);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Aspect ratio"
          className="shrink-0 flex items-center gap-1.5 px-2.5 h-7 rounded-lg border bg-white/[0.04] border-white/[0.1] text-white/85 hover:text-white hover:bg-white/[0.07] hover:border-white/20 transition-all text-xs font-medium"
        >
          <div className="flex items-center justify-center" style={{ width: 14, height: 14 }}>
            <div
              className="rounded-[2px] border border-white/60"
              style={{
                width: Math.max(6, primary.previewW * 0.4),
                height: Math.max(6, primary.previewH * 0.4),
              }}
            />
          </div>
          <span className="font-mono">{primary.ratio}</span>
          {extraCount > 0 && (
            <span className="text-[10px] font-mono text-[#ef3e29]">+{extraCount}</span>
          )}
          <ChevronDown className="w-3 h-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        side="top"
        sideOffset={10}
        className="w-64 p-1.5 bg-[#0c0e14] border border-white/[0.1] rounded-2xl shadow-2xl shadow-black/60"
      >
        <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-white/40">
          Aspect ratio
        </div>
        <div className="flex flex-col">
          {FORMATS.map((f) => {
            const isSelected = selectedFormats.includes(f.id);
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => toggleFormat(f.id)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-left ${
                  isSelected ? "bg-white/[0.08]" : "hover:bg-white/[0.04]"
                }`}
              >
                <div className="flex items-center justify-center w-8 h-8 shrink-0">
                  <div
                    className={`rounded-sm border ${isSelected ? "border-white bg-white/15" : "border-white/45"}`}
                    style={{ width: f.previewW * 0.6, height: f.previewH * 0.6 }}
                  />
                </div>
                <div className="flex flex-col items-start leading-tight flex-1 min-w-0">
                  <span className="text-[13px] text-white font-medium">{f.ratio}</span>
                  <span className="text-[10px] text-white/45">
                    {f.label} · {f.size}
                  </span>
                </div>
                {isSelected && <Check className="w-4 h-4 text-white shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * VP-326: najlepsza kreacja z `/api/ad-creator/best-creatives` — ten sam obiekt
 * trafia do stanu, wiec opisany raz.
 */
type BestCreative = {
  adId: string;
  adName: string;
  adSetName: string;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  spend: string;
  impressions: string;
  clicks: string;
  ctr: string;
  leads: number;
  costPerLead: number;
  purchases: number;
  costPerPurchase: number;
  primaryText: string;
  headline: string;
  callToAction: string;
};

export default function AdGraphicsBuilder({
  selectedModel,
  initialPrompt,
  initialLayout,
  initialImageModel,
  contentType = "ad",
  inspirationContext,
  campaignHandoff,
}: AdGraphicsBuilderProps) {
  const isOrganic = contentType === "organic";
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation("adGraphicsBuilder");

  // Upload one inspiration image to S3 and return its signed URL. Goes through
  // `uploadFile` (multipart) which strips image metadata before sending, so the
  // WAF XSS rule never fires (VP-146). Defined up top so the auto-fill effects
  // below can reference it without a temporal-dead-zone error.
  const uploadInspirationToS3 = useCallback(async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append("file", file);
    const res = (await uploadFile("/api/ad-creator/inspiration-asset", fd)) as { url?: string };
    if (!res?.url) throw new Error("Brak URL z uploadu");
    return res.url;
  }, []);

  // Rotating loading messages for pending states
  const copyLoadingMessage = useRotatingMessage(COPY_LOADING_KEYS);
  const generateLoadingMessage = useRotatingMessage(GENERATE_LOADING_KEYS);

  // Client picker for agency users — global store
  const {
    selectedClientId: globalClientId,
    selectedClientUserId: globalClientUserId,
    clients: globalAgencyClients,
    isAgencyUser,
    setSelectedClient: setGlobalSelectedClient,
  } = useAgencyClients();
  const selectedClientId = globalClientId ?? "";
  // useMemo: bez tego `|| []` tworzyło nową tablicę przy każdym renderze, przez co
  // useCallback niżej rekonstruował się bez potrzeby.
  const agencyClients = useMemo(
    () =>
      (globalAgencyClients as unknown as Array<{
        id: string;
        name: string;
        offerSheetId: string | null;
        userId?: string;
        adAccounts?: { id: string; name: string; platform?: string | null }[] | null;
      }>) || [],
    [globalAgencyClients]
  );
  const selectedClient = agencyClients.find((c) => c.id === selectedClientId);

  const handleClientSelect = useCallback(
    (clientId: string) => {
      const client = agencyClients.find((c) => c.id === clientId);
      if (client)
        setGlobalSelectedClient({
          id: client.id,
          userId: client.userId ?? "",
          name: client.name,
        });
      setSelectedAssetIds(new Set());
      const firstMetaAccount = client?.adAccounts?.find(
        (a) => !a.platform || a.platform === "meta"
      );
      setBestCreativesAdAccountId(firstMetaAccount?.id ?? "");
      if (!client?.offerSheetId) return;
      apiRequest("PUT", `/api/offer-sheets/${client.offerSheetId}/activate`).then(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/offer-sheets/active"] });
      });
    },
    [agencyClients, setGlobalSelectedClient]
  );

  // Auto-apply side-effects when global client changes
  useEffect(() => {
    if (selectedClientId) handleClientSelect(selectedClientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  // Offer sheet
  const { data: activeOfferSheet } = useQuery<{
    id: string;
    title: string;
    content: string;
  } | null>({
    queryKey: ["/api/offer-sheets/active"],
    queryFn: async () => {
      try {
        return await apiRequest("GET", "/api/offer-sheets/active");
      } catch {
        return null;
      }
    },
  });

  // Main fields
  const [prompt, setPrompt] = useState(() => summarizeImagePrompt(initialPrompt ?? ""));
  // VP-179: gdy widoczny prompt jest streszczeniem struktury JSON, trzymamy tu oryginał
  // wysyłany do generatora — razem ze streszczeniem, dla którego powstał. Payload liczy się
  // tylko dopóki pole zawiera dokładnie to streszczenie; każda inna zmiana (edycja, wyczyszczenie,
  // wariacja, handoff z kampanii) unieważnia go sama z siebie, więc user nigdy nie wygeneruje
  // czegoś innego niż widzi.
  const promptPayloadRef = useRef<{ summary: string; payload: string } | null>(null);
  const applyStructuredPrompt = useCallback((summary: string, payload: string) => {
    promptPayloadRef.current = { summary, payload };
    setPrompt(summary);
  }, []);
  const resolvePromptForApi = useCallback(
    (visible: string): string => resolveStructuredPrompt(visible, promptPayloadRef.current),
    []
  );
  /**
   * Wstawia do pola prompt pochodzący z zapisanej grafiki (galeria inspiracji, edycja
   * z biblioteki). Zwykły tekst idzie bez zmian; strukturę JSON widać jako streszczenie.
   */
  const showSavedPrompt = useCallback(
    (raw: string) => {
      const summary = summarizeImagePrompt(raw);
      if (summary === raw) setPrompt(raw);
      else if (summary) applyStructuredPrompt(summary, raw);
      else setPrompt("");
    },
    [applyStructuredPrompt]
  );
  const [quantity, setQuantity] = useState(isOrganic ? 1 : 2);

  // VP-231: last prompt the YT-thumbnail suggestion toast was shown for (suggest once per prompt)
  const ytSuggestedPromptRef = useRef<string | null>(null);

  // Auto-grow prompt textarea (caps at max-height with internal scroll)
  const promptTextareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = promptTextareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 240) + "px";
  }, [prompt]);

  // Pre-fill prompt when coming from library edit
  useEffect(() => {
    // VP-179: zapisane grafiki mogą mieć w prompcie strukturę JSON — nie pokazujemy jej surowo.
    if (initialPrompt) showSavedPrompt(initialPrompt);
  }, [initialPrompt, showSavedPrompt]);

  // Auto-fill from campaign creator handoff prop (step 4 inline embed)
  const campaignHandoffProcessed = useRef(false);
  useEffect(() => {
    if (!campaignHandoff || campaignHandoffProcessed.current) return;
    campaignHandoffProcessed.current = true;

    if (campaignHandoff.adPrompt) {
      setPrompt(campaignHandoff.adPrompt);
    }

    if (campaignHandoff.referenceImages.length > 0) {
      setShowInspiration(true);
      // Initial population from a campaign handoff — clamp to the inspiration limit.
      const toUpload = campaignHandoff.referenceImages.slice(0, MAX_INSPIRATION_FILES);
      // Push handed-off creatives to S3 — never carry their base64 into the
      // generate-graphics body (VP-146). On upload failure the creative is skipped
      // rather than re-inlined as base64 (which would revive the 403).
      void (async () => {
        const uploaded = (
          await Promise.all(
            toUpload.map(async (img) => {
              try {
                const blob = await (await fetch(img.dataUrl)).blob();
                const file = new File([blob], img.name, { type: blob.type || "image/jpeg" });
                const remoteUrl = await uploadInspirationToS3(file);
                return { name: img.name, url: remoteUrl, preview: remoteUrl };
              } catch {
                return null;
              }
            })
          )
        ).filter((x): x is { name: string; url: string; preview: string } => x !== null);
        if (uploaded.length > 0) setInspirationFiles((prev) => [...prev, ...uploaded]);
      })();
    }
  }, [campaignHandoff, uploadInspirationToS3]);

  // Auto-fill from ROAS World best creatives inspiration
  const inspirationContextProcessed = useRef(false);
  useEffect(() => {
    if (!inspirationContext || inspirationContextProcessed.current) return;
    inspirationContextProcessed.current = true;

    const { adName, primaryText, headline, imageUrl } = inspirationContext;

    // Build prompt from ad copy
    const parts = [
      "Stwórz wariacje grafik reklamowych inspirowane najlepiej działającą reklamą.",
      "",
      `Nazwa reklamy: "${adName}"`,
      headline ? `Nagłówek: "${headline}"` : "",
      primaryText ? `Tekst reklamy: "${primaryText}"` : "",
      "",
      "Zachowaj podobny styl wizualny i kompozycję, ale stwórz nowe, unikalne warianty.",
    ]
      .filter(Boolean)
      .join("\n");
    setPrompt(parts);

    setInspirationNote(
      "Zachowaj styl, kompozycję i elementy, które działają w oryginalnej reklamie."
    );
    setShowInspiration(true);

    // Fetch the image, push it to S3 and reference the signed URL (no base64 in
    // the request body — VP-146). Fall back to the source URL if the upload fails.
    if (imageUrl) {
      const name = `inspiration-${adName.slice(0, 20).replace(/\s+/g, "-")}.jpg`;
      void (async () => {
        try {
          const blob = await (await fetch(imageUrl)).blob();
          const file = new File([blob], name, { type: blob.type || "image/jpeg" });
          const remoteUrl = await uploadInspirationToS3(file);
          setInspirationFiles([{ name, url: imageUrl, preview: remoteUrl }]);
        } catch {
          setInspirationFiles([{ name, url: imageUrl, preview: imageUrl }]);
        }
      })();
    }
  }, [inspirationContext, uploadInspirationToS3]);
  const [selectedFormats, setSelectedFormats] = useState<AdFormat[]>(["feed"]);
  const [style, setStyle] = useState<GraphicStyle | null>(null);
  const [showStyleSection, setShowStyleSection] = useState(false);
  const [imageModel, setImageModel] = useState(initialImageModel ?? "nano-banana-pro");

  // When deep-linked with a different image model, switch to it
  useEffect(() => {
    if (initialImageModel) setImageModel(initialImageModel);
  }, [initialImageModel]);
  const [resolution, setResolution] = useState<"0.5K" | "1K" | "2K" | "4K">("1K");
  const [funnelType, setFunnelType] = useState<FunnelType>("");
  const [landingPageUrl, setLandingPageUrl] = useState("");
  const [generationMode, setGenerationMode] = useState<GenerationMode>("auto");

  // Reset resolution to 1K if current model doesn't support it
  useEffect(() => {
    const supported = IMAGE_RESOLUTIONS.filter((r) => r.models.includes(imageModel));
    if (!supported.some((r) => r.id === resolution)) {
      setResolution("1K");
    }
    // Reset ma się dziać na ZMIANĘ modelu; `resolution` jest tu tylko odczytywane.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageModel]);

  // Active avatar (multi-avatar persona context, localStorage-backed)
  const {
    avatars: allAvatars,
    activeAvatar,
    setActiveAvatar,
    createAvatar,
    updateAvatar,
  } = useAvatars();
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const { recordUsage: recordRecentPrompt } = useRecentPrompts();
  const [dismissedSuggestionFor, setDismissedSuggestionFor] = useState<string | null>(null);

  // Inspiration source picker — controls which input is highlighted/visible
  const [inspirationSource, setInspirationSource] = useState<
    "link" | "upload" | "pinterest" | "instagram"
  >("link");

  // Expandable sections
  const [showProduct, setShowProduct] = useState(false);
  const [showBrand, setShowBrand] = useState(false);
  const [showInspiration, setShowInspiration] = useState(false);
  // Product modal (UGC studio backend) — same picker as in UGC creator.
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<UgcProduct | null>(null);
  // Inspiration popup — short add-flow instead of expanding the giant inline panel.
  const [inspirationDialogOpen, setInspirationDialogOpen] = useState(false);
  // References (brand-assets) popup — multi-select existing + quick upload, no big panel.
  const [referencesDialogOpen, setReferencesDialogOpen] = useState(false);
  const [refsBulkUploading, setRefsBulkUploading] = useState(false);
  const refsDialogInputRef = useRef<HTMLInputElement>(null);
  const [showLayout, setShowLayout] = useState(false);

  // Craft prompt with AI — modal where user types a rough idea, AI returns a rich JSON-structured prompt
  const [craftModalOpen, setCraftModalOpen] = useState(false);
  const [craftIdea, setCraftIdea] = useState("");
  const [craftedPrompt, setCraftedPrompt] = useState("");
  // VP-179: pełna struktura JSON stojąca za widocznym streszczeniem — nigdy nie trafia do UI.
  // Trzymana razem ze streszczeniem, żeby dało się poznać, czy user go nie przerobił.
  const [craftedPromptPayload, setCraftedPromptPayload] = useState<{
    summary: string;
    payload: string;
  } | null>(null);
  // Optional style-reference image for "Prompt + AI" — holds an S3 signed URL
  // (uploaded via loadCraftRefImage), never base64 (VP-146B).
  const [craftRefImage, setCraftRefImage] = useState<string | null>(null);
  const [craftRefUploading, setCraftRefUploading] = useState(false);
  const [craftRefDragOver, setCraftRefDragOver] = useState(false);

  // Product details
  const [productName, setProductName] = useState("");
  const [productPrice, setProductPrice] = useState("");

  // Brand details
  const [brandName, setBrandName] = useState("");
  const [brandColors, setBrandColors] = useState("");
  const [ctaText, setCtaText] = useState("");
  const [brandBookText, setBrandBookText] = useState("");
  const [brandBookFileName, setBrandBookFileName] = useState("");
  const [brandBookUploading, setBrandBookUploading] = useState(false);
  const brandBookInputRef = useRef<HTMLInputElement>(null);

  // Brand assets
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());
  const [assetUploadOpen, setAssetUploadOpen] = useState(false);
  const [assetName, setAssetName] = useState("");
  const [assetDescription, setAssetDescription] = useState("");
  const [assetFileType, setAssetFileType] = useState<string>("person");
  const [assetFile, setAssetFile] = useState<File | null>(null);
  const [assetPreview, setAssetPreview] = useState<string | null>(null);
  const [assetUploading, setAssetUploading] = useState(false);
  const assetInputRef = useRef<HTMLInputElement>(null);

  // Visual inspiration. `preview` holds the durable reference URL used for generation
  // (S3 signed URL after upload, or a remote URL for library/example picks); `url`
  // is the instant local thumbnail. `uploading` is true while the file is being
  // pushed to S3 (VP-146 — references go to S3, never base64 in the request body).
  const [inspirationFiles, setInspirationFiles] = useState<
    { name: string; url: string; preview?: string; uploading?: boolean }[]
  >([]);
  const [inspirationDragOver, setInspirationDragOver] = useState(false);
  // Unified user media library — wszystko co user kiedykolwiek wgrał (firm-assets + UGC avatars + HeyGen + uploads)
  const { data: allUserMedia } = useAllUserMedia();
  const allMedia: UnifiedMediaItem[] = allUserMedia?.items ?? [];
  const [mediaFilter, setMediaFilter] = useState<"all" | UnifiedMediaItem["category"]>("all");
  const [inspirationNote, setInspirationNote] = useState("");
  const inspirationInputRef = useRef<HTMLInputElement>(null);
  const [inspirationUrl, setInspirationUrl] = useState("");
  const [inspirationUrlLoading, setInspirationUrlLoading] = useState(false);

  // Pinterest board inspiration
  const [pinterestUrl, setPinterestUrl] = useState("");
  const [pinterestLoading, setPinterestLoading] = useState(false);
  const [pinterestPreviews, setPinterestPreviews] = useState<
    Array<{ url: string; imageUrl: string; selected: boolean }>
  >([]);

  // E-commerce: paste shop/product URL → scrape product images
  const [ecomShopUrl, setEcomShopUrl] = useState("");
  const [ecomScraping, setEcomScraping] = useState(false);
  const [ecomScrapedImages, setEcomScrapedImages] = useState<string[]>([]);
  const [ecomScrapeError, setEcomScrapeError] = useState<string | null>(null);
  const [isEcommerceMode, setIsEcommerceMode] = useState<boolean>(
    campaignHandoff?.businessModel === "ecommerce_direct"
  );

  // When user flips into e-commerce mode, default funnel type to 'ecommerce' if not set yet
  useEffect(() => {
    if (isEcommerceMode && !funnelType) {
      setFunnelType("ecommerce");
    }
  }, [isEcommerceMode, funnelType]);

  const handleEcomScrape = async () => {
    const url = ecomShopUrl.trim();
    if (!url) return;
    setEcomScraping(true);
    setEcomScrapeError(null);
    setEcomScrapedImages([]);
    try {
      const resp = await fetch("/api/campaign-creator/scrape-product", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ url }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error((err as { message?: string }).message || `HTTP ${resp.status}`);
      }
      const data = (await resp.json()) as { imageUrls?: string[] };
      const images = (data.imageUrls ?? []).slice(0, 6);
      if (images.length === 0) {
        setEcomScrapeError(t("ecomMode.noImagesError"));
        return;
      }
      setEcomScrapedImages(images);
      setInspirationFiles((prev) => [
        ...prev,
        ...images.map((u, i) => ({ name: `product-${i + 1}`, url: u, preview: u })),
      ]);
    } catch (err) {
      setEcomScrapeError(err instanceof Error ? err.message : t("ecomMode.genericError"));
    } finally {
      setEcomScraping(false);
    }
  };

  // Layout
  const [selectedLayout, setSelectedLayout] = useState<string | null>(initialLayout ?? null);

  // When deep-linked with a new layout, apply it
  useEffect(() => {
    if (initialLayout) setSelectedLayout(initialLayout);
  }, [initialLayout]);
  const [selectedExampleId, setSelectedExampleId] = useState<string | null>(null);

  // Prompt optimization toggle — OFF by default for organic (raw prompt), always ON for ads
  const [optimizePromptEnabled, setOptimizePromptEnabled] = useState(!isOrganic);

  // YouTube thumbnail from transcript modal
  const [ytThumbModalOpen, setYtThumbModalOpen] = useState(false);
  const [ytTranscript, setYtTranscript] = useState("");

  // Results state
  const [graphics, setGraphics] = useState<GeneratedGraphic[]>([]);
  // ── Active generation job (shared background-job store, VP-113) ─────────────
  // /generate-graphics returns 202 + jobId and runs in the background (no long
  // SSE — that 504'd behind CloudFront). The active jobId lives in a store OUTSIDE
  // this component, so leaving and returning to the view resumes the SAME live
  // polling (progress + progressive graphics) instead of losing the generation.
  // VP-206: lista równoległych jobów (store dedupuje po id, kolejność zgłoszenia).
  const activeGenJobIds =
    useBackgroundJobsStore((s) => s.activeJobIdsByKind.graphic) ?? NO_ACTIVE_GEN_JOBS;
  const addActiveGraphicJob = useBackgroundJobsStore((s) => s.addActiveJob);
  const removeActiveGraphicJob = useBackgroundJobsStore((s) => s.removeActiveJob);
  // Postęp i etykiety (prompt) per job — do panelu kolejki. Czyszczone razem
  // z jobem; po unmount przepadają tylko etykiety, joby wracają ze store'a.
  const [genJobProgress, setGenJobProgress] = useState<Record<string, number>>({});
  // Etykiety (prompt) per job — trzymane też PO zakończeniu joba, bo nagłówki
  // sekcji galerii ("Zadanie N — prompt") potrzebują ich do końca sesji.
  const [genJobPrompts, setGenJobPrompts] = useState<Record<string, string>>({});
  // Przypisanie grafika → job, na potrzeby sekcji galerii per zadanie.
  const [graphicJobByGraphicId, setGraphicJobByGraphicId] = useState<Record<string, string>>({});
  // Stabilny numer "Zadanie N" nadawany RAZ przy rejestracji joba — panel kolejki
  // i nagłówki sekcji galerii używają tego samego numeru (indeks żywej listy
  // przesuwał się po zakończeniu joba, a kolejność grup zależała od tempa dostaw).
  const [genJobTaskNo, setGenJobTaskNo] = useState<Record<string, number>>({});
  const taskCounterRef = useRef(0);
  // Joby adoptowane ze stale'owego ?job= w URL — ich awarie pollingu (404 po
  // usunięciu joba) sprzątamy po cichu, zamiast straszyć toastem o nieudanym
  // generowaniu, gdy realne joby usera idą poprawnie.
  const urlAdoptedJobsRef = useRef<Set<string>>(new Set());
  const registerGenJob = useCallback(
    (jobId: string, promptLabel?: string) => {
      setGenJobTaskNo((prev) => {
        if (prev[jobId] !== undefined) return prev;
        taskCounterRef.current += 1;
        return { ...prev, [jobId]: taskCounterRef.current };
      });
      if (promptLabel) {
        setGenJobPrompts((prev) => (prev[jobId] ? prev : { ...prev, [jobId]: promptLabel }));
      }
      addActiveGraphicJob("graphic", jobId);
    },
    [addActiveGraphicJob]
  );
  // Returning via the VP-logo badge / deep link (?job=...): seed the store once so
  // the live polling path below adopts it — same UI as a fresh generation.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const urlJob = url.searchParams.get("job");
    if (urlJob) {
      urlAdoptedJobsRef.current.add(urlJob);
      registerGenJob(urlJob);
      // Parametr znika z URL-a po adopcji — kolejne remounty widoku nie
      // adoptowałyby w kółko dawno zakończonego joba (powtarzane toasty błędu).
      url.searchParams.delete("job");
      window.history.replaceState(window.history.state, "", url.toString());
    }
    // Run once on mount; the store dedupes an already-tracked job.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // VP-206: store jobów żyje w pamięci karty — po refreshu przepadał, a backend
  // dalej liczył joby do limitu (429 przy pustym panelu). Odzyskujemy aktywne
  // joby ad-graphic z serwera: na mount oraz po odbitym 429 (etykiety w DTO).
  const adoptActiveJobsFromServer = useCallback(async () => {
    try {
      const data = (await apiRequest("GET", "/api/jobs/active")) as {
        jobs?: Array<{ id: string; type?: string; promptLabel?: string }>;
      };
      if (!Array.isArray(data.jobs)) return;
      for (const job of data.jobs) {
        if (job.type === "ad-graphic") registerGenJob(job.id, job.promptLabel);
      }
    } catch {
      // Cicho — brak re-adopcji nie psuje niczego poza wygodą; badge jobów
      // i tak pokaże aktywne generowania.
    }
  }, [registerGenJob]);
  useEffect(() => {
    void adoptActiveJobsFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [lastGenMeta, setLastGenMeta] = useState<{
    tokensDeducted: number;
    tokensRemaining: number;
  } | null>(null);
  const handedOffRef = useRef<Set<string>>(new Set());
  // VP-206: sprzątanie po jobie (koniec/błąd/awaria pollingu) — znika ze store'a
  // i z lokalnych map panelu kolejki; pozostałe joby lecą dalej.
  const finishGenJob = useCallback(
    (jobId: string) => {
      removeActiveGraphicJob("graphic", jobId);
      setGenJobProgress((prev) => {
        const next = { ...prev };
        delete next[jobId];
        return next;
      });
      // genJobPrompts celowo zostaje — etykieta sekcji w galerii żyje dłużej niż job.
    },
    [removeActiveGraphicJob]
  );
  // campaignHandoff bywa przekazywany jako inline-literal (animated-welcome-chat)
  // — trzymamy go w refie, żeby tożsamość handlera snapshotów nie zależała od
  // re-renderów rodzica (każda zmiana tożsamości refire'uje efekty trackerów).
  const campaignHandoffRef = useRef(campaignHandoff);
  useEffect(() => {
    campaignHandoffRef.current = campaignHandoff;
  }, [campaignHandoff]);
  // Snapshot joba z trackera: merge grafik po id (równoległe joby nie nadpisują
  // sobie wyników), progres do panelu kolejki, done/error kończy tylko ten job.
  const handleGenJobSnapshot = useCallback(
    (jobId: string, snapshot: GenJobSnapshot) => {
      if (typeof snapshot.progress === "number") {
        const progress = snapshot.progress;
        setGenJobProgress((prev) =>
          prev[jobId] === progress ? prev : { ...prev, [jobId]: progress }
        );
      }
      // Etykieta z DTO — joby adoptowane po refreshu/deep-linku nie mają jej lokalnie.
      if (snapshot.promptLabel) {
        const promptLabel = snapshot.promptLabel;
        setGenJobPrompts((prev) => (prev[jobId] ? prev : { ...prev, [jobId]: promptLabel }));
      }
      const tagIncoming = (incoming: Array<{ id: string }>) =>
        setGraphicJobByGraphicId((prev) => {
          if (incoming.every((g) => prev[g.id] === jobId)) return prev;
          const next = { ...prev };
          for (const g of incoming) next[g.id] = jobId;
          return next;
        });
      if (snapshot.graphics?.length) {
        const incoming = snapshot.graphics;
        setGraphics((prev) => mergeGraphicsById(prev, incoming));
        tagIncoming(incoming);
        // Hand newly-arrived images off to the campaign flow (once each).
        const onGenerated = campaignHandoffRef.current?.onGenerated;
        if (onGenerated) {
          for (const g of incoming) {
            if (g.imageUrl && !handedOffRef.current.has(g.imageUrl)) {
              handedOffRef.current.add(g.imageUrl);
              void onGenerated(g.imageUrl);
            }
          }
        }
      } else if (snapshot.status === "done" && snapshot.resultUrls?.length) {
        const fallbackGraphics = snapshot.resultUrls.map((url, i) => ({
          id: url,
          format: "square" as AdFormat,
          variantIndex: i,
          imageUrl: url,
          prompt: "",
          size: "",
          imageModel: "",
        }));
        setGraphics((prev) => mergeGraphicsById(prev, fallbackGraphics));
        tagIncoming(fallbackGraphics);
      }
      if (snapshot.status === "done") {
        if (snapshot.meta) {
          // Suma sesji, nie ostatni job — galeria akumuluje wyniki wielu zadań,
          // więc stopka tokenów też sumuje; "pozostało" z najświeższego joba.
          const meta = snapshot.meta;
          setLastGenMeta((prev) => ({
            tokensDeducted: (prev?.tokensDeducted ?? 0) + meta.tokensDeducted,
            tokensRemaining: meta.tokensRemaining,
          }));
        }
        finishGenJob(jobId);
        void queryClient.invalidateQueries({ queryKey: ["/api/graphics"] });
        void queryClient.invalidateQueries({ queryKey: ["jobs", "active"] });
      } else if (snapshot.status === "error") {
        // Stale ?job= z URL-a, który skończył się błędem dawno temu — sprzątamy
        // bez straszenia toastem przy każdym wejściu na widok.
        if (!urlAdoptedJobsRef.current.has(jobId)) {
          toast({
            title: "Nie udało się wygenerować grafik",
            description: snapshot.errorMessage ?? "Spróbuj ponownie.",
            variant: "destructive",
          });
        }
        finishGenJob(jobId);
      }
    },
    [finishGenJob, toast]
  );
  // Awaria samego pollingu (timeout / błąd GET /api/jobs/:id) — bez tego wpis
  // w kolejce wisiałby wiecznie. Toast + sprzątnięcie tylko tego joba.
  const handleGenJobPollFailure = useCallback(
    (jobId: string, failure: JobPollFailure) => {
      // 404 po adopcji stale'owego ?job= (job usunięty/cudzy) — cicho sprzątnij,
      // realne generowania usera idą dalej i nie zasługują na czerwony toast.
      if (!(urlAdoptedJobsRef.current.has(jobId) && failure.kind === "request_error")) {
        toast({
          title: "Nie udało się wygenerować grafik",
          description:
            failure.kind === "timeout"
              ? "Przekroczono czas oczekiwania — spróbuj ponownie."
              : failure.message,
          variant: "destructive",
        });
      }
      finishGenJob(jobId);
    },
    [finishGenJob, toast]
  );

  // VP-119: regeneracja pojedynczej grafiki też jedzie jako async job (202 + polling),
  // bo synchroniczna odpowiedź przebijała limit CloudFront → 504. Czekamy aż job się skończy,
  // wtedy dopisujemy nowy wariant do listy. Błąd niesie konkretny powód (timeout/content policy/...).
  const [activeRegenJobId, setActiveRegenJobId] = useState<string | null>(null);
  const { job: regenJobData, pollFailure: regenPollFailure } =
    useJobPolling<GenJobSnapshot>(activeRegenJobId);
  useEffect(() => {
    if (!regenJobData || !activeRegenJobId) return;
    if (regenJobData.status === "done") {
      const variant = regenJobData.graphics?.[0];
      if (variant) {
        setGraphics((prev) => [...prev, variant as GeneratedGraphic]);
        // VP-206: wariant dziedziczy sekcję (zadanie) edytowanej grafiki.
        const sourceJobId = editingGraphicId ? graphicJobByGraphicId[editingGraphicId] : undefined;
        if (sourceJobId) {
          setGraphicJobByGraphicId((prev) => ({ ...prev, [variant.id]: sourceJobId }));
        }
      }
      setEditingGraphicId(null);
      setEditInstructions("");
      setActiveRegenJobId(null);
      void queryClient.invalidateQueries({ queryKey: ["/api/graphics"] });
      void queryClient.invalidateQueries({ queryKey: ["jobs", "active"] });
    } else if (regenJobData.status === "error") {
      toast({
        title: "Nie udało się wygenerować wariantu",
        description: regenJobData.errorMessage ?? "Spróbuj ponownie.",
        variant: "destructive",
      });
      setActiveRegenJobId(null);
    }
    // Efekt reaguje na zmianę statusu zadania i pokazuje toast — celowo trzymamy wąskie
    // zależności, żeby zmiana czegokolwiek innego nie odpaliła go po raz drugi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regenJobData, activeRegenJobId]);
  useEffect(() => {
    if (!regenPollFailure || !activeRegenJobId) return;
    toast({
      title: "Nie udało się wygenerować wariantu",
      description:
        regenPollFailure.kind === "timeout"
          ? "Przekroczono czas oczekiwania — spróbuj ponownie."
          : regenPollFailure.message,
      variant: "destructive",
    });
    setActiveRegenJobId(null);
    // Jak wyżej — toast ma polecieć dokładnie raz na nieudane pollowanie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regenPollFailure, activeRegenJobId]);
  const [expandedImage, setExpandedImage] = useState<string | null>(null);
  const [editingGraphicId, setEditingGraphicId] = useState<string | null>(null);
  const [editInstructions, setEditInstructions] = useState("");
  const [selectedGraphicIds, setSelectedGraphicIds] = useState<Set<string>>(new Set());
  const [graphicFeedback, setGraphicFeedback] = useState<Record<string, "up" | "down">>({});
  const [feedbackReasonId, setFeedbackReasonId] = useState<string | null>(null);
  const [feedbackReasonText, setFeedbackReasonText] = useState("");
  const [previewCopyMode, setPreviewCopyMode] = useState(false);
  const [copyPreview, setCopyPreview] = useState<
    Array<{ headline: string; primaryText: string; ctaText: string }>
  >([]);

  // Store access for cross-module transfer
  const { setCurrentModule, setPendingCampaignCreatives } = useStore();

  // ── Best creatives from Meta ad account ──────────────
  const [showBestCreatives, setShowBestCreatives] = useState(false);
  const [bestCreativesAdAccountId, setBestCreativesAdAccountId] = useState("");
  const [excludedBestCreativeIds, setExcludedBestCreativeIds] = useState<Set<string>>(new Set());
  const [bestCreativesDatePreset, setBestCreativesDatePreset] = useState("last_7d");
  const [bestCreativesFetching, setBestCreativesFetching] = useState(false);
  const [bestCreatives, setBestCreatives] = useState<BestCreative[]>([]);

  const { data: metaStatusData } = useQuery<{
    connected: boolean;
    adAccounts?: { id: string; name: string }[];
  }>({
    queryKey: ["meta-status-ad-gfx"],
    queryFn: () => apiRequest("GET", "/api/integrations/meta/status"),
    staleTime: 60_000,
  });
  const adAccountsForCreatives = selectedClient?.adAccounts?.length
    ? selectedClient.adAccounts
        .filter((a) => !a.platform || a.platform === "meta")
        .map((a) => ({ id: a.id, name: a.name }))
    : metaStatusData?.adAccounts || [];

  const fetchBestCreatives = useCallback(async () => {
    if (!bestCreativesAdAccountId) return;
    setBestCreativesFetching(true);
    try {
      const data = await apiRequest<{ creatives?: BestCreative[] }>(
        "GET",
        `/api/ad-creator/best-creatives?adAccountId=${encodeURIComponent(bestCreativesAdAccountId)}&datePreset=${bestCreativesDatePreset}&limit=3`
      );
      setBestCreatives(data.creatives || []);
      setExcludedBestCreativeIds(new Set());
    } catch (err) {
      // eslint-disable-next-line no-console -- jedyny slad bledu; apps/web nie ma loggera
      console.error("Failed to fetch best creatives:", (err as Error).message);
      setBestCreatives([]);
    } finally {
      setBestCreativesFetching(false);
    }
  }, [bestCreativesAdAccountId, bestCreativesDatePreset]);

  const handleUseCreativesAsInspiration = useCallback(async () => {
    const eligible = bestCreatives.filter(
      (c) => !excludedBestCreativeIds.has(c.adId) && (c.imageUrl || c.thumbnailUrl)
    );
    if (eligible.length === 0) return;

    const slots = availableInspirationSlots(inspirationFiles.length);
    if (slots <= 0) {
      toast({
        title: t("inspiration.limitTitle"),
        description: t("inspiration.limitDesc", { max: MAX_INSPIRATION_FILES }),
        variant: "destructive",
      });
      return;
    }
    const withImages = eligible.slice(0, slots);
    if (eligible.length > slots) {
      toast({
        title: t("inspiration.limitTitle"),
        description: t("inspiration.limitDesc", { max: MAX_INSPIRATION_FILES }),
        variant: "destructive",
      });
    }

    // Download each Meta creative and push it to S3 (Meta CDN URLs aren't reliably
    // fetchable by the image model, and base64 in the body breaks the request —
    // VP-146). If the download/upload fails, skip the creative rather than ship a
    // Meta CDN URL the provider can't fetch (it would fail loudly at generation).
    const newInspirationFiles: typeof inspirationFiles = [];
    for (const creative of withImages) {
      const imgUrl = creative.imageUrl || creative.thumbnailUrl || "";
      const name = `best-ad-${creative.adId.slice(-6)}.jpg`;
      try {
        const response = await fetch(imgUrl);
        const blob = await response.blob();
        const file = new File([blob], name, { type: blob.type || "image/jpeg" });
        const remoteUrl = await uploadInspirationToS3(file);
        newInspirationFiles.push({ name, url: imgUrl, preview: remoteUrl });
      } catch {
        // Skip — no durable, provider-fetchable reference for this creative.
      }
    }
    if (newInspirationFiles.length > 0)
      setInspirationFiles((prev) => [...prev, ...newInspirationFiles]);
    setShowInspiration(true);

    // Build context from best-performing ads
    const creativesSummary = withImages
      .map(
        (c, i) =>
          `Reklama ${i + 1}: "${c.headline || c.adName}" — CTR: ${c.ctr}%, Tekst: "${c.primaryText || "—"}"`
      )
      .join("\n");

    const variationPrompt = [
      "Stwórz wariacje grafik reklamowych inspirowane najlepiej działającymi reklamami na tym koncie.",
      "Zachowaj podobny styl wizualny, kompozycję i energy tych grafik, ale stwórz nowe, unikalne warianty.",
      "",
      "Najlepsze reklamy na koncie:",
      creativesSummary,
    ].join("\n");

    setPrompt(variationPrompt);
    setInspirationNote(
      "Stwórz wariacje na podstawie najlepiej działających grafik — zachowaj styl, kompozycję i elementy, które działają."
    );
  }, [bestCreatives, excludedBestCreativeIds, inspirationFiles, uploadInspirationToS3, toast, t]);

  const DATE_PRESETS: { value: string; label: string }[] = [
    { value: "last_7d", label: t("datePresets.last7d") },
    { value: "last_14d", label: t("datePresets.last14d") },
    { value: "last_30d", label: t("datePresets.last30d") },
    { value: "last_90d", label: t("datePresets.last90d") },
  ];

  const toggleGraphicSelection = useCallback((id: string) => {
    setSelectedGraphicIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleSendToCampaignCreator = useCallback(() => {
    const selected = graphics.filter((g) => selectedGraphicIds.has(g.id));
    if (selected.length === 0) return;
    setPendingCampaignCreatives(
      selected.map((g) => ({
        url: g.imageUrl,
        name: `${g.format}-v${g.variantIndex}.png`,
        prompt: g.prompt,
      }))
    );
    setCurrentModule("campaign-creator");
  }, [graphics, selectedGraphicIds, setPendingCampaignCreatives, setCurrentModule]);

  const handleGraphicFeedback = useCallback(
    async (graphicId: string, rating: "up" | "down") => {
      const current = graphicFeedback[graphicId];
      const newRating = current === rating ? undefined : rating;
      setGraphicFeedback((prev) => {
        const next = { ...prev };
        if (newRating) {
          next[graphicId] = newRating;
        } else {
          delete next[graphicId];
        }
        return next;
      });
      // Open reason input when setting a rating, close when toggling off
      if (newRating) {
        setFeedbackReasonId(graphicId);
        setFeedbackReasonText("");
      } else {
        setFeedbackReasonId(null);
        setFeedbackReasonText("");
      }
      const graphic = graphics.find((g) => g.id === graphicId);
      if (!graphic) return;
      try {
        await apiRequest("POST", "/api/ad-creator/graphic-feedback", {
          graphicId,
          rating: newRating || null,
          prompt: graphic.prompt,
          imageModel: graphic.imageModel,
          format: graphic.format,
          ...(funnelType ? { funnelType } : {}),
        });
      } catch (err) {
        // eslint-disable-next-line no-console -- jedyny slad bledu; apps/web nie ma loggera
        console.error("Feedback error:", err);
      }
    },
    [graphicFeedback, graphics, funnelType]
  );

  const handleSubmitFeedbackReason = useCallback(
    async (graphicId: string) => {
      if (!feedbackReasonText.trim()) {
        setFeedbackReasonId(null);
        return;
      }
      const graphic = graphics.find((g) => g.id === graphicId);
      const rating = graphicFeedback[graphicId];
      if (!graphic || !rating) return;
      try {
        await apiRequest("POST", "/api/ad-creator/graphic-feedback", {
          graphicId,
          rating,
          prompt: graphic.prompt,
          imageModel: graphic.imageModel,
          format: graphic.format,
          reason: feedbackReasonText.trim(),
          ...(funnelType ? { funnelType } : {}),
        });
      } catch (err) {
        // eslint-disable-next-line no-console -- jedyny slad bledu; apps/web nie ma loggera
        console.error("Feedback reason error:", err);
      }
      setFeedbackReasonId(null);
      setFeedbackReasonText("");
    },
    [feedbackReasonText, graphics, graphicFeedback, funnelType]
  );

  const handleSendAllToCampaignCreator = useCallback(() => {
    if (graphics.length === 0) return;
    setPendingCampaignCreatives(
      graphics.map((g) => ({
        url: g.imageUrl,
        name: `${g.format}-v${g.variantIndex}.png`,
        prompt: g.prompt,
      }))
    );
    setCurrentModule("campaign-creator");
  }, [graphics, setPendingCampaignCreatives, setCurrentModule]);

  // Format toggle
  const toggleFormat = useCallback((format: AdFormat) => {
    setSelectedFormats((prev) =>
      prev.includes(format) ? prev.filter((f) => f !== format) : [...prev, format]
    );
  }, []);

  // File upload handler
  // Shared file ingestion — used by both the file picker and drag&drop. Each file
  // shows instantly (local object URL) then is pushed to S3; the resulting URL
  // becomes the reference used for generation. No base64 ever enters the payload.
  const processInspirationFiles = useCallback(
    (files: FileList | File[]) => {
      const incoming = Array.from(files).filter(
        (f) => f.type.startsWith("image/") && f.size <= 10 * 1024 * 1024
      );
      if (incoming.length === 0) return;

      const slots = availableInspirationSlots(inspirationFiles.length);
      if (slots <= 0) {
        toast({
          title: t("inspiration.limitTitle"),
          description: t("inspiration.limitDesc", { max: MAX_INSPIRATION_FILES }),
          variant: "destructive",
        });
        return;
      }
      const accepted = incoming.slice(0, slots);
      if (incoming.length > slots) {
        toast({
          title: t("inspiration.limitTitle"),
          description: t("inspiration.limitDesc", { max: MAX_INSPIRATION_FILES }),
          variant: "destructive",
        });
      }

      const optimistic = accepted.map((file) => ({
        name: file.name,
        url: URL.createObjectURL(file),
        uploading: true,
      }));
      setInspirationFiles((prev) => [...prev, ...optimistic]);

      optimistic.forEach((entry, i) => {
        void uploadInspirationToS3(accepted[i])
          .then((remoteUrl) => {
            // Display switches to the S3 URL — free the local blob URL.
            URL.revokeObjectURL(entry.url);
            setInspirationFiles((cur) =>
              cur.map((it) =>
                it.url === entry.url ? { ...it, preview: remoteUrl, uploading: false } : it
              )
            );
          })
          .catch(() => {
            URL.revokeObjectURL(entry.url);
            setInspirationFiles((cur) => cur.filter((it) => it.url !== entry.url));
            toast({
              title: t("inspiration.uploadFailedTitle"),
              description: t("inspiration.uploadFailedDesc"),
              variant: "destructive",
            });
          });
      });
    },
    [inspirationFiles, uploadInspirationToS3, toast, t]
  );

  const handleInspirationUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) processInspirationFiles(e.target.files);
      e.target.value = "";
    },
    [processInspirationFiles]
  );

  const handleInspirationDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setInspirationDragOver(false);
      if (e.dataTransfer?.files?.length) processInspirationFiles(e.dataTransfer.files);
    },
    [processInspirationFiles]
  );

  // Paste an image straight from the clipboard (Ctrl/Cmd+V) while the modal is open.
  useEffect(() => {
    if (!inspirationDialogOpen) return;
    const onPaste = (e: ClipboardEvent) => {
      const files = Array.from(e.clipboardData?.items ?? [])
        .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
        .map((item) => item.getAsFile())
        .filter((f): f is File => !!f);
      if (files.length) {
        e.preventDefault();
        processInspirationFiles(files);
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [inspirationDialogOpen, processInspirationFiles]);

  // "Prompt + AI" style-reference image: upload to S3 and keep the signed URL —
  // never base64 in the request body (VP-146B, same WAF/payload class as inspirations).
  const loadCraftRefImage = useCallback(
    (file: File | null | undefined) => {
      if (!file || !file.type.startsWith("image/") || file.size > 10 * 1024 * 1024) return;
      setCraftRefUploading(true);
      void uploadInspirationToS3(file)
        .then((remoteUrl) => setCraftRefImage(remoteUrl))
        .catch(() =>
          toast({
            title: t("inspiration.uploadFailedTitle"),
            description: t("inspiration.uploadFailedDesc"),
            variant: "destructive",
          })
        )
        .finally(() => setCraftRefUploading(false));
    },
    [uploadInspirationToS3, toast, t]
  );

  const handleCraftRefDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setCraftRefDragOver(false);
      loadCraftRefImage(e.dataTransfer?.files?.[0]);
    },
    [loadCraftRefImage]
  );

  // Paste a style-reference image while the "Prompt + AI" modal is open.
  useEffect(() => {
    if (!craftModalOpen) return;
    const onPaste = (e: ClipboardEvent) => {
      const file = Array.from(e.clipboardData?.items ?? [])
        .find((item) => item.kind === "file" && item.type.startsWith("image/"))
        ?.getAsFile();
      if (file) {
        e.preventDefault();
        loadCraftRefImage(file);
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [craftModalOpen, loadCraftRefImage]);

  const removeInspirationFile = useCallback((index: number) => {
    setInspirationFiles((prev) => {
      const removed = prev[index];
      if (removed?.url?.startsWith("blob:")) URL.revokeObjectURL(removed.url);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // Fetch inspiration image from URL (Instagram, Facebook, Ad Library)
  const handleInspirationUrlFetch = useCallback(async () => {
    const trimmed = inspirationUrl.trim();
    if (!trimmed) return;

    if (inspirationFiles.length >= MAX_INSPIRATION_FILES) {
      toast({
        title: t("inspiration.limitTitle"),
        description: t("inspiration.limitDesc", { max: MAX_INSPIRATION_FILES }),
        variant: "destructive",
      });
      return;
    }

    setInspirationUrlLoading(true);
    try {
      const data = (await apiRequest("POST", "/api/ad-creator/fetch-inspiration-url", {
        url: trimmed,
      })) as { url: string; source: string };
      setInspirationFiles((prev) => [
        ...prev,
        {
          name: `link-${data.source}-${Date.now()}.jpg`,
          url: data.url,
          preview: data.url,
        },
      ]);
      setInspirationUrl("");
      setShowInspiration(true);
    } catch (err: unknown) {
      let description = t("toasts.urlDefaultError");
      if (err instanceof Error) {
        try {
          const parsed = JSON.parse(err.message.replace(/^\d+:\s*/, ""));
          description = parsed.message || err.message;
        } catch {
          description = err.message;
        }
      }
      toast({ title: t("toasts.urlFetchFailed"), description, variant: "destructive" });
    } finally {
      setInspirationUrlLoading(false);
    }
  }, [inspirationUrl, inspirationFiles, toast, t]);

  // Pinterest board fetch handler
  const handlePinterestBoardFetch = useCallback(async () => {
    const trimmed = pinterestUrl.trim();
    if (!trimmed) return;

    setPinterestLoading(true);
    setPinterestPreviews([]);
    try {
      const data = (await apiRequest("POST", "/api/ad-creator/fetch-pinterest-board", {
        url: trimmed,
        maxImages: 12,
      })) as {
        images: Array<{ url: string; imageUrl: string }>;
        boardUrl: string;
        totalFound: number;
      };

      if (data.images.length === 0) {
        toast({
          title: t("toasts.noPins"),
          description: t("toasts.noPinsDesc"),
          variant: "destructive",
        });
        return;
      }

      setPinterestPreviews(data.images.map((img) => ({ ...img, selected: true })));
      setShowInspiration(true);
      toast({
        title: t("toasts.pinterestTitle"),
        description: t("toasts.pinterestFetched", { count: data.images.length }),
      });
    } catch (err: unknown) {
      let description = t("toasts.pinterestDefaultError");
      if (err instanceof Error) {
        try {
          const parsed = JSON.parse(err.message.replace(/^\d+:\s*/, ""));
          description = parsed.message || err.message;
        } catch {
          description = err.message;
        }
      }
      toast({ title: t("toasts.pinterestFetchFailed"), description, variant: "destructive" });
    } finally {
      setPinterestLoading(false);
    }
  }, [pinterestUrl, toast, t]);

  const handlePinterestTogglePin = useCallback((index: number) => {
    setPinterestPreviews((prev) =>
      prev.map((p, i) => (i === index ? { ...p, selected: !p.selected } : p))
    );
  }, []);

  const handlePinterestConfirm = useCallback(() => {
    const selected = pinterestPreviews.filter((p) => p.selected);
    if (selected.length === 0) return;

    const slots = availableInspirationSlots(inspirationFiles.length);
    if (slots <= 0) {
      toast({
        title: t("inspiration.limitTitle"),
        description: t("inspiration.limitDesc", { max: MAX_INSPIRATION_FILES }),
        variant: "destructive",
      });
      return;
    }
    const picked = selected.slice(0, slots);
    if (selected.length > slots) {
      toast({
        title: t("inspiration.limitTitle"),
        description: t("inspiration.limitDesc", { max: MAX_INSPIRATION_FILES }),
        variant: "destructive",
      });
    }

    const newFiles = picked.map((pin, idx) => ({
      name: `pinterest-${idx + 1}-${Date.now()}.jpg`,
      url: pin.imageUrl,
      preview: pin.imageUrl,
    }));
    setInspirationFiles((prev) => [...prev, ...newFiles]);
    setPinterestPreviews([]);
    setPinterestUrl("");
    toast({
      title: t("toasts.pinterestTitle"),
      description: t("toasts.pinterestAdded", { count: picked.length }),
    });
  }, [pinterestPreviews, inspirationFiles, toast, t]);

  // Brand book upload handler
  const handleBrandBookUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    setBrandBookUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const resp = await fetch("/api/ad-creator/upload-brand-book", {
        method: "POST",
        headers: { Authorization: `Bearer ${getAuthToken() || ""}` },
        body: formData,
      });
      if (!resp.ok) throw new Error("Upload failed");
      const data = await resp.json();
      setBrandBookFileName(data.fileName);
      if (data.hasText) {
        setBrandBookText(data.extractedText);
      }
    } catch (err) {
      // eslint-disable-next-line no-console -- jedyny slad bledu; apps/web nie ma loggera
      console.error("Brand book upload error:", err);
    } finally {
      setBrandBookUploading(false);
    }
  }, []);

  // ── Brand Assets (persistent, Supabase Storage) ──────
  const brandAssetsClientParam = selectedClientId ? `?clientId=${selectedClientId}` : "";
  const { data: brandAssetsData, refetch: refetchBrandAssets } = useQuery<BrandAsset[]>({
    queryKey: ["brand-assets", selectedClientId],
    queryFn: () => apiRequest("GET", `/api/ad-creator/brand-assets${brandAssetsClientParam}`),
    staleTime: 60_000,
  });
  const brandAssets = brandAssetsData || [];

  const handleAssetFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setAssetFile(file);
    const reader = new FileReader();
    reader.onload = () => setAssetPreview(reader.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handleAssetUpload = useCallback(async () => {
    if (!assetFile || !assetName.trim() || !assetDescription.trim()) return;
    // Defensive gate: agency users MUST pick a client first. Without it the
    // upload would either succeed but get orphaned (clientId=null on the row)
    // or fail with a confusing 403 if the user has no agency yet.
    if (isAgencyUser && !selectedClientId) {
      toast({
        title: "Wybierz klienta",
        description:
          "Najpierw wybierz klienta w lewym górnym selektorze, do którego dodajesz materiały.",
        variant: "destructive",
      });
      return;
    }
    setAssetUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", await stripImageMetadata(assetFile), assetFile.name);
      formData.append("name", assetName.trim());
      formData.append("description", assetDescription.trim());
      formData.append("fileType", assetFileType);
      if (selectedClientId) formData.append("clientId", selectedClientId);
      const resp = await fetch("/api/ad-creator/brand-assets", {
        method: "POST",
        headers: { Authorization: `Bearer ${getAuthToken() || ""}` },
        body: formData,
      });
      if (!resp.ok) {
        const errorData = await resp.json().catch(() => null);
        throw new Error(errorData?.message || `Upload failed (${resp.status})`);
      }
      setAssetFile(null);
      setAssetPreview(null);
      setAssetName("");
      setAssetDescription("");
      setAssetUploadOpen(false);
      refetchBrandAssets();
      toast({
        title: t("toasts.assetUploaded"),
        description: t("toasts.assetUploadedDesc", { name: assetName.trim() }),
      });
    } catch (err: unknown) {
      // eslint-disable-next-line no-console -- jedyny slad bledu; apps/web nie ma loggera
      console.error("Brand asset upload error:", err);
      toast({
        title: "Wgraj jeszcze raz",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    } finally {
      setAssetUploading(false);
    }
  }, [
    assetFile,
    assetName,
    assetDescription,
    assetFileType,
    selectedClientId,
    refetchBrandAssets,
    toast,
    isAgencyUser,
    t,
  ]);

  const handleRefsBulkUpload = useCallback(
    async (files: File[]): Promise<void> => {
      if (files.length === 0) return;
      if (isAgencyUser && !selectedClientId) {
        toast({
          title: "Wybierz klienta",
          description:
            "Najpierw wybierz klienta w selektorze — bez tego zdjęcia referencyjne nie zostaną przypisane.",
          variant: "destructive",
        });
        return;
      }
      setRefsBulkUploading(true);
      try {
        for (const file of files) {
          const formData = new FormData();
          const baseName = file.name.replace(/\.[^.]+$/, "").slice(0, 60) || "reference";
          formData.append("file", await stripImageMetadata(file), file.name);
          formData.append("name", baseName);
          formData.append("description", "Zdjęcie referencyjne");
          formData.append("fileType", "photo");
          if (selectedClientId) formData.append("clientId", selectedClientId);
          const resp = await fetch("/api/ad-creator/brand-assets", {
            method: "POST",
            headers: { Authorization: `Bearer ${getAuthToken() || ""}` },
            body: formData,
          });
          if (!resp.ok) {
            const errorData = await resp.json().catch(() => null);
            throw new Error(errorData?.message || `Upload failed (${resp.status})`);
          }
          const created = (await resp.json().catch(() => null)) as { id?: string } | null;
          if (created?.id) {
            setSelectedAssetIds((prev) => {
              const next = new Set(prev);
              next.add(created.id!);
              return next;
            });
          }
        }
        await refetchBrandAssets();
        toast({ title: "Wgrano", description: `${files.length} zdj. referencyjnych` });
      } catch (err) {
        toast({
          title: "Nie udało się wgrać wszystkich plików",
          description: FriendlyErrorMapper.map(err).description,
          variant: "destructive",
        });
      } finally {
        setRefsBulkUploading(false);
      }
    },
    [selectedClientId, isAgencyUser, refetchBrandAssets, toast]
  );

  const handleAssetDelete = useCallback(
    async (id: string) => {
      try {
        await apiRequest("DELETE", `/api/ad-creator/brand-assets/${id}`);
        setSelectedAssetIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        refetchBrandAssets();
      } catch (err) {
        // eslint-disable-next-line no-console -- jedyny slad bledu; apps/web nie ma loggera
        console.error("Brand asset delete error:", err);
      }
    },
    [refetchBrandAssets]
  );

  const toggleAssetSelection = useCallback((id: string) => {
    setSelectedAssetIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Build full description for the API from all fields
  const buildDescription = (): string => {
    // VP-179: do modelu leci pełna struktura, jeśli widoczne pole to wciąż jej streszczenie.
    let desc = resolvePromptForApi(prompt);
    if (productName || productPrice) {
      desc += `\n\nProdukt: ${productName}`;
      if (productPrice) desc += ` | Cena: ${productPrice}`;
    }
    if (brandName) desc += `\nMarka: ${brandName}`;
    if (activeAvatar) {
      desc += `\n\nAvatar: ${activeAvatar.name}`;
      if (activeAvatar.contextText)
        desc += `\nKontekst persony: ${activeAvatar.contextText.substring(0, 2000)}`;
      if (activeAvatar.voiceRefs.length > 0)
        desc += `\nReferencje stylu: ${activeAvatar.voiceRefs.join(", ")}`;
      if (activeAvatar.usePhotoByDefault && activeAvatar.photoUrl) {
        desc += `\nZdjęcie avatara (użyj jako referencji wizualnej): ${activeAvatar.photoUrl}`;
      }
    }
    if (brandBookText) desc += `\nBrand book: ${brandBookText.substring(0, 3000)}`;
    const selectedBrandAssets = brandAssets.filter((a) => selectedAssetIds.has(a.id));
    if (selectedBrandAssets.length > 0) {
      desc += "\n\nBrand assets do użycia w grafice:";
      selectedBrandAssets.forEach((a) => {
        desc += `\n- [${a.fileType}] ${a.name}: ${a.description} (URL: ${a.fileUrl})`;
      });
    }
    if (selectedLayout) {
      const layout = LAYOUT_TEMPLATES.find((l) => l.id === selectedLayout);
      if (layout) desc += `\n\nUkład grafiki: ${layout.label} — ${layout.desc}`;
    }
    if (selectedLayout === "youtube-thumbnail") {
      const faceAssets = selectedBrandAssets.filter(
        (a) => a.fileType === "person" || a.fileType === "photo"
      );
      const faceLine =
        faceAssets.length > 0
          ? `Użyj TWARZY z załączonego pliku marki (${faceAssets.map((a) => a.name).join(", ")}) — duża, ostra, z wyraźną emocją (zaskoczenie / ekscytacja), zajmuje ~40-50% kadru po lewej stronie. Zachowaj wierność tej twarzy.`
          : `Po lewej stronie duża twarz osoby (~40-50% kadru) z wyraźną emocją (zaskoczenie / ekscytacja). Jeśli w plikach marki jest zdjęcie osoby — użyj go.`;
      desc += `\n\n=== YOUTUBE THUMBNAIL TEMPLATE (1280×720, 16:9) ===
- Format: pozioma miniaturka YouTube 16:9, czytelna nawet w rozmiarze 320×180px w feedzie.
- Kompozycja: ${faceLine}
- Tekst: 3-5 słów MAX, ogromna pogrubiona typografia po prawej stronie (sans-serif, condensed/black weight). Tekst musi być natychmiast czytelny na małym ekranie.
- Kolorystyka: jeden dominujący kolor akcentu (czerwony / żółty / zielony), wysoki kontrast tła i pierwszego planu, żywe nasycone kolory.
- Styl YT: lekki outline / glow wokół postaci żeby odcięła się od tła, drobne strzałki / koła / podkreślenia jako akcenty wizualne.
- Cel: maksymalny CTR — clickbait visual, ale spójny z marką. Bez znaków wodnych, bez bordersów, bez logo YouTube.
=== END TEMPLATE ===`;
    }
    if (inspirationNote) desc += `\n\nInspiracja wizualna: ${inspirationNote}`;
    return desc;
  };

  // Compute variants per format based on quantity
  const variantsPerFormat = Math.max(1, Math.ceil(quantity / Math.max(selectedFormats.length, 1)));
  // VP-146: MULTIPLE inspirations = 1 graphic each. The graphic count is locked to the number of
  // inspirations (the backend generates one graphic per inspiration); the manual quantity stepper
  // is replaced by a read-only indicator.
  // VP-224: a SINGLE inspiration (e.g. the "best ad from account" path) keeps the stepper active —
  // the backend generates the requested number of variations of that one inspiration.
  const inspirationDriven = inspirationFiles.length > 1;
  const effectiveQuantity = inspirationDriven ? inspirationFiles.length : quantity;

  // ── Generate mutation ──────────────────────────────────

  const generateMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const token = getAuthToken();
      // Generation runs as a BACKGROUND JOB now (returns 202 + jobId instantly so
      // CloudFront never 504s a long response). Progressive results arrive via the
      // job poller above (genJobData) — not from this request.
      const response = await fetch("/api/ad-creator/generate-graphics", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const text = await response.text();
        // VP-206: backend pilnuje limitu równoległych jobów — 429 niesie
        // zlokalizowaną kopertę { code, message } (errorBody, wzorzec VP-231);
        // pokazujemy message verbatim. Frontend blokuje przycisk wcześniej, ale
        // druga karta / wyścig requestów wciąż może się o limit odbić. Przy 429
        // synchronizujemy też store z serwerem — po refreshu karty joby żyły na
        // backendzie, a panel kolejki był pusty ("limit" bez widocznego powodu).
        if (response.status === 429) {
          const parsed = parseServerErrorBody(text);
          void adoptActiveJobsFromServer();
          throw new Error(
            parsed?.message ?? t("queue.limitReached", { limit: MAX_CONCURRENT_AD_GRAPHIC_JOBS })
          );
        }
        throw new Error(`${response.status}: ${text}`);
      }

      const json = (await response.json()) as { jobId: string };
      return json;
    },
    onSuccess: ({ jobId }, variables) => {
      // VP-206: wyniki się akumulują — nowe zadanie dokłada kolejną sekcję
      // galerii, nie kasuje poprzednich (stan i tak resetuje się przy wyjściu
      // z widoku, bo grafiki żyją w lokalnym stanie komponentu).
      // VP-179: etykieta zadania to tekst dla człowieka — bierzemy widoczny prompt,
      // nie `productDescription`, bo tam siedzi pełna struktura wysyłana do modelu.
      const fallbackLabel =
        typeof variables.productDescription === "string"
          ? summarizeImagePrompt(variables.productDescription)
          : "";
      registerGenJob(jobId, prompt.trim() || fallbackLabel);
    },
  });

  // VP-206: pojedyncze aktywne generowanie NIE blokuje kolejnego promptu —
  // przycisk blokuje dopiero pełna pula slotów (limit współdzielony z backendem).
  const anyGenerating = generateMutation.isPending || activeGenJobIds.length > 0;
  const genSlotsFull = activeGenJobIds.length >= MAX_CONCURRENT_AD_GRAPHIC_JOBS;
  const generating = generateMutation.isPending || genSlotsFull;

  const regenerateMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const resp = await apiRequest("POST", "/api/ad-creator/regenerate-graphic", data);
      return resp as { jobId: string };
    },
    onSuccess: ({ jobId }) => {
      // Generacja leci w tle — czekamy na job (polling powyżej), nie na odpowiedź POST.
      setActiveRegenJobId(jobId);
    },
    onError: (err: Error) => {
      toast({
        title: "Nie udało się wygenerować wariantu",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    },
  });

  // True while the regenerate POST is in flight OR its background job is still running.
  const regenerating = regenerateMutation.isPending || !!activeRegenJobId;

  const optimizePromptMutation = useMutation({
    mutationFn: async (description: string) => {
      const resp = await apiRequest("POST", "/api/ad-creator/optimize-prompt", {
        description,
        selectedModel,
        contentType,
      });
      return resp as {
        optimizedPrompt: string;
        originalLength: number;
        optimizedLength: number;
        meta: { tokensDeducted: number; tokensRemaining: number };
      };
    },
  });

  const craftPromptMutation = useMutation({
    mutationFn: async (idea: string) => {
      const aspectLabel =
        selectedFormats.length === 1
          ? (
              {
                feed: "Feed (4:5) 1080x1350",
                story: "Story (9:16) 1080x1920",
                square: "Square (1:1) 1080x1080",
                landscape: "Landscape (16:9) 1920x1080",
                banner: "Banner (1.91:1) 1200x628",
              } as Record<string, string>
            )[selectedFormats[0]]
          : undefined;
      const resp = await apiRequest("POST", "/api/ad-creator/craft-prompt", {
        idea,
        selectedModel,
        ...(selectedFormats[0] ? { format: selectedFormats[0] } : {}),
        ...(aspectLabel ? { aspectLabel } : {}),
        ...(craftRefImage ? { referenceImage: craftRefImage } : {}),
      });
      return resp as {
        craftedPrompt: string;
        promptPayload: string;
        meta: { tokensDeducted: number; tokensRemaining: number };
      };
    },
    onSuccess: (data, idea) => {
      // VP-179: w polu pokazujemy streszczenie, strukturę trzymamy z boku dla generatora.
      // Gdy streszczenia nie da się zbudować, pokazujemy pomysł użytkownika i NIE trzymamy
      // payloadu — inaczej pole mówiłoby co innego, niż dostaje model.
      const summary = data.craftedPrompt.trim();
      setCraftedPrompt(summary || idea);
      setCraftedPromptPayload(
        summary && data.promptPayload ? { summary, payload: data.promptPayload } : null
      );
    },
    onError: (err: Error) => {
      toast({
        title: "Nie udalo sie wygenerowac promptu",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    },
  });

  const ytThumbnailFromTranscriptMutation = useMutation({
    mutationFn: async (transcript: string) => {
      const resp = await apiRequest("POST", "/api/ad-creator/youtube-thumbnail-from-transcript", {
        transcript,
        selectedModel,
      });
      return resp as {
        suggestion: { hookText: string; emotion: string; topic: string; visualPrompt: string };
        meta: { tokensDeducted: number; tokensRemaining: number };
      };
    },
    onSuccess: (data) => {
      const { hookText, visualPrompt } = data.suggestion;
      setPrompt(`${visualPrompt}\n\nText on thumbnail: "${hookText}"`);
      setSelectedLayout("youtube-thumbnail");
      setSelectedFormats(["landscape"]);
      setOptimizePromptEnabled(false);
      setResolution("2K");
      setYtThumbModalOpen(false);
      setYtTranscript("");
      toast({
        title: "Gotowe!",
        description: `Hook: "${hookText}". Sprawdź prompt i kliknij Generuj.`,
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Nie udało się wygenerować propozycji",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    },
  });

  const copyPreviewMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const resp = await apiRequest("POST", "/api/ad-creator/generate-copy-preview", data);
      return resp as {
        variants: Array<{ headline: string; primaryText: string; ctaText: string }>;
      };
    },
    onSuccess: (data) => {
      setCopyPreview(data.variants);
    },
  });

  // (iluzja postępu żyje teraz per wiersz w QueueRow — patrz komponent wyżej)

  const handleGenerate = async () => {
    if (!prompt.trim() || selectedFormats.length === 0) return;
    // VP-179: w historii trzymamy pełną strukturę — przy wstawianiu i tak pokazujemy streszczenie.
    recordRecentPrompt(resolvePromptForApi(prompt));

    // VP-231: YT thumbnail detection is a SUGGESTION only — the user's format choice always wins.
    // The old version force-switched to landscape and aborted, looping on every Generate click.
    // Suggested at most once per prompt so repeated generations aren't nagged.
    if (
      looksLikeYtThumbnail(prompt) &&
      !selectedFormats.includes("landscape") &&
      ytSuggestedPromptRef.current !== prompt
    ) {
      ytSuggestedPromptRef.current = prompt;
      toast({
        title: t("ytDetect.title"),
        description: t("ytDetect.description"),
        action: (
          <ToastAction
            altText={t("ytDetect.switchAction")}
            onClick={() => {
              setSelectedFormats(["landscape"]);
              if (!selectedLayout) setSelectedLayout("youtube-thumbnail");
            }}
          >
            {t("ytDetect.switchAction")}
          </ToastAction>
        ),
      });
    }

    let description = buildDescription();
    // VP-231: surowy opis SPRZED optymalizatora — optymalizator celowo wycina szczegóły biznesowe
    // pod prompt wizualny, więc copy musi powstawać z oryginalnej treści usera, nie z okrojonej.
    const copySourceText = description.slice(0, 10000);

    // Optimize prompt: always for ads, only when toggle is ON for organic
    if (optimizePromptEnabled) {
      try {
        const optimized = await optimizePromptMutation.mutateAsync(description);
        description = optimized.optimizedPrompt;
      } catch {
        description = description.slice(0, 9500);
      }
    }

    // VP-206: NIE czyścimy wyników — nowe zadanie dokłada sekcję galerii,
    // poprzednie sekcje (i zaznaczenia/feedback ich grafik) zostają.

    // If preview mode is on and we don't have approved copy yet, generate preview first
    if (previewCopyMode && copyPreview.length === 0) {
      copyPreviewMutation.mutate({
        productDescription: description,
        copySourceText,
        formats: selectedFormats,
        variantsPerFormat,
        ...(funnelType ? { funnelType } : {}),
        ...(landingPageUrl ? { landingPageUrl } : {}),
        ...(ctaText ? { ctaText } : {}),
        selectedModel,
      });
      return; // stop here — user will review copy and click "Generuj grafiki" again
    }

    // Collect selected brand asset photos for edit (person swap / scene swap)
    const selectedEditAssets: Array<{
      url: string;
      name: string;
      description: string;
      fileType?: string;
    }> = brandAssets
      .filter(
        (a) =>
          selectedAssetIds.has(a.id) &&
          (a.fileType === "photo" || a.fileType === "person" || a.fileType === "scene")
      )
      .map((a) => ({
        url: a.fileUrl,
        name: a.name,
        description: a.description,
        fileType: a.fileType,
      }));

    // Active avatar (UGC studio persona) — its photo URL must reach the image edit API as a
    // person reference, otherwise the model can't preserve the face. Without this push the URL
    // would only appear inside the text prompt, which image models can't fetch.
    if (activeAvatar?.photoUrl) {
      const alreadyIncluded = selectedEditAssets.some((a) => a.url === activeAvatar.photoUrl);
      if (!alreadyIncluded) {
        selectedEditAssets.push({
          url: activeAvatar.photoUrl,
          name: activeAvatar.name,
          description: activeAvatar.contextText?.slice(0, 200) || "",
          fileType: "person",
        });
      }
    }

    // Block generation while any inspiration is still uploading to S3 — otherwise
    // the request would go out with an incomplete reference set (VP-146).
    if (inspirationFiles.some((f) => f.uploading)) {
      toast({
        title: t("inspiration.stillUploadingTitle"),
        description: t("inspiration.stillUploadingDesc"),
        variant: "destructive",
      });
      return;
    }

    // VP-146: user inspirations map 1:1 to graphics (inspiration[i] → graphic[i]), so they travel
    // in their OWN field. Global references (example template, brand logo/graphics, product photos)
    // enrich EVERY graphic and stay in referenceImages.
    const inspirationImages: string[] = inspirationFiles
      .map((f) => f.preview)
      .filter(Boolean) as string[];
    const refImages: string[] = [];
    if (selectedExampleId) {
      const selectedExample = EXAMPLE_TEMPLATES.find((ex) => ex.id === selectedExampleId);
      if (selectedExample?.thumbnail) refImages.push(selectedExample.thumbnail);
    }
    // Include selected logo / graphic brand assets as visual reference images so the model
    // can actually see and reuse them (otherwise URL-in-prompt is useless for image models)
    brandAssets
      .filter(
        (a) => selectedAssetIds.has(a.id) && (a.fileType === "logo" || a.fileType === "graphic")
      )
      .forEach((a) => {
        if (a.fileUrl) refImages.push(a.fileUrl);
      });

    // E-commerce: include selected product photos as visual references
    if (isEcommerceMode && selectedProduct?.imageUrls?.length) {
      selectedProduct.imageUrls.forEach((u) => {
        if (u) refImages.push(u);
      });
    }

    if (generationMode === "edit" && inspirationImages.length === 0 && refImages.length === 0) {
      toast({
        title: "Tryb Edit wymaga inspiracji",
        description: t("toasts.editModeDesc"),
        variant: "destructive",
      });
      return;
    }

    generateMutation.mutate({
      productDescription: description,
      copySourceText,
      style: style || undefined,
      formats: selectedFormats,
      variantsPerFormat,
      ctaText: ctaText || undefined,
      colorScheme: brandColors || undefined,
      additionalInstructions: inspirationNote || undefined,
      brandBookContext: brandBookText || undefined,
      imageModel,
      selectedModel,
      generationMode,
      contentType,
      resolution,
      ...(globalClientUserId ? { forUserId: globalClientUserId } : {}),
      ...(funnelType ? { funnelType } : {}),
      ...(landingPageUrl ? { landingPageUrl } : {}),
      ...(refImages.length > 0 ? { referenceImages: refImages } : {}),
      ...(inspirationImages.length > 0 ? { inspirationImages } : {}),
      ...(selectedEditAssets.length > 0 ? { brandPersonPhotos: selectedEditAssets } : {}),
      ...(copyPreview.length > 0 ? { approvedCopy: copyPreview } : {}),
      ...(selectedLayout ? { selectedLayout } : {}),
      ...(isEcommerceMode ? { rawPromptMode: true } : {}),
    });

    // Clear copy preview after sending to generation
    if (copyPreview.length > 0) setCopyPreview([]);
  };

  const handleRegenerate = (graphic: GeneratedGraphic) => {
    regenerateMutation.mutate({
      productDescription: buildDescription(),
      style: style || undefined,
      format: graphic.format,
      editInstructions: editInstructions || undefined,
      originalImageUrl: graphic.imageUrl || undefined,
      originalPrompt: graphic.prompt || undefined,
      ctaText: ctaText || undefined,
      colorScheme: brandColors || undefined,
      imageModel: graphic.imageModel || imageModel,
      selectedModel,
      resolution,
      contentType,
      ...(globalClientUserId ? { forUserId: globalClientUserId } : {}),
      ...(selectedLayout ? { selectedLayout } : {}),
    });
  };

  const handleDownload = async (graphic: GeneratedGraphic) => {
    const response = await fetch(graphic.imageUrl);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reklama-${graphic.format}-v${graphic.variantIndex}.png`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // VP-206: wyniki akumulują się między zadaniami — to jedyne miejsce, gdzie
  // user może świadomie wyczyścić galerię (np. nieudana seria przed wysyłką
  // "wszystkie do kampanii"). Zablokowane w trakcie generowania, bo snapshoty
  // biegnących jobów i tak dosłałyby swoje grafiki z powrotem.
  const clearAllResults = () => {
    setGraphics([]);
    setSelectedGraphicIds(new Set());
    setGraphicFeedback({});
    setGraphicJobByGraphicId({});
    setGenJobPrompts({});
    setGenJobTaskNo({});
    taskCounterRef.current = 0;
    setLastGenMeta(null);
    handedOffRef.current.clear();
  };

  const handleDownloadAll = async () => {
    for (const g of graphics) {
      await handleDownload(g);
      await new Promise((r) => setTimeout(r, 300));
    }
  };

  // ── Render ─────────────────────────────────────────────

  const hasGraphics = graphics.length > 0 || anyGenerating;

  // VP-206: sekcje galerii per zadanie ("Zadanie 1 → grafiki, Zadanie 2 → ...").
  const graphicsGroups = useMemo(
    () => groupGraphicsByJob(graphics, graphicJobByGraphicId),
    [graphics, graphicJobByGraphicId]
  );

  // Preview example template
  const [previewExample, setPreviewExample] = useState<ExampleTemplate | null>(null);

  // Use example template — fetch prompt from backend
  const handleUseExample = useCallback(async (example: ExampleTemplate) => {
    if (example.style) setStyle(example.style);
    if (example.layout) setSelectedLayout(example.layout);
    setSelectedExampleId(example.id);
    try {
      const data = await apiRequest<{ prompt?: string }>(
        "GET",
        `/api/ad-creator/example-templates/${example.id}/prompt`
      );
      if (data.prompt) setPrompt(data.prompt);
    } catch {
      // eslint-disable-next-line no-console -- jedyny slad bledu; apps/web nie ma loggera
      console.error("Failed to fetch example prompt");
    }
  }, []);

  // ── Inspiration pool: curated templates + Viral Partners gallery ──────────────
  // Shown 4 at a time; "Wylosuj kolejne" rotates through the whole pool so users can
  // pick any inspiration and build a graphic from it.
  const { data: galleryData } = useQuery<{
    items: Array<{ id: string; signedUrl: string | null; prompt: string | null }>;
  }>({
    queryKey: ["viral-gallery", "inspiration"],
    queryFn: () => apiRequest("GET", "/api/viral-gallery?mediaType=image"),
    staleTime: 5 * 60_000,
  });

  type InspoCard =
    | { kind: "template"; key: string; label: string; thumbnail: string; ex: ExampleTemplate }
    | { kind: "gallery"; key: string; label: string; thumbnail: string; prompt: string | null };

  const inspoPool = useMemo<InspoCard[]>(() => {
    // Skip index 0 (placeholder cyberpunk thumbnail is a broken "Image Error" asset).
    const templates: InspoCard[] = EXAMPLE_TEMPLATES.slice(1).map((ex) => ({
      kind: "template",
      key: `t-${ex.id}`,
      label: ex.label,
      thumbnail: ex.thumbnail,
      ex,
    }));
    const gallery: InspoCard[] = (galleryData?.items ?? [])
      .filter((it) => !!it.signedUrl)
      .map((it) => ({
        kind: "gallery",
        key: `g-${it.id}`,
        label: "Inspiracja z galerii",
        thumbnail: it.signedUrl as string,
        prompt: it.prompt,
      }));
    return [...templates, ...gallery];
  }, [galleryData]);

  const [inspoOffset, setInspoOffset] = useState(0);
  const visibleInspo = useMemo<InspoCard[]>(() => {
    if (inspoPool.length === 0) return [];
    const n = Math.min(4, inspoPool.length);
    return Array.from({ length: n }, (_, i) => inspoPool[(inspoOffset + i) % inspoPool.length]);
  }, [inspoPool, inspoOffset]);

  // ── Roulette reel for "Wylosuj kolejne" (CS:GO-style horizontal scroll) ────────
  // One long horizontal strip scrolls left and decelerates (ease-out) to land on
  // the next 4 inspirations — like a roulette wheel coasting to a stop.
  const REEL_SPIN_CARDS = 24; // random cards scrolled past before the result
  const REEL_SPIN_DURATION = 3.4; // seconds
  const [spinning, setSpinning] = useState(false);
  const [reelStrip, setReelStrip] = useState<InspoCard[]>([]);
  const [reelX, setReelX] = useState(0);
  const reelViewportRef = useRef<HTMLDivElement>(null);
  const spinTimers = useRef<number[]>([]);
  useEffect(
    () => () => {
      spinTimers.current.forEach(clearTimeout);
    },
    []
  );

  const shuffleInspo = useCallback(() => {
    if (spinning || inspoPool.length <= 4) return;
    const n = Math.min(4, inspoPool.length);
    const targetOffset = (inspoOffset + 4) % inspoPool.length;
    const finalCards = Array.from(
      { length: n },
      (_, i) => inspoPool[(targetOffset + i) % inspoPool.length]
    );
    const current = visibleInspo;
    const randCard = () => inspoPool[Math.floor(Math.random() * inspoPool.length)];
    // current 4 (start) → REEL_SPIN_CARDS randoms → final 4 (landing).
    const strip = [...current, ...Array.from({ length: REEL_SPIN_CARDS }, randCard), ...finalCards];

    setSpinning(true);
    setReelStrip(strip);
    setReelX(0);

    // Next frame: measure viewport width, then translate so the LAST 4 land in view.
    const startT = window.setTimeout(() => {
      const w = reelViewportRef.current?.clientWidth ?? 0;
      const cardW = w / 4;
      setReelX(-(strip.length - 4) * cardW);
    }, 40);
    spinTimers.current.push(startT);

    const endT = window.setTimeout(
      () => {
        setInspoOffset(targetOffset);
        setSpinning(false);
        setReelStrip([]);
        setReelX(0);
      },
      40 + REEL_SPIN_DURATION * 1000 + 200
    );
    spinTimers.current.push(endT);
  }, [spinning, inspoPool, inspoOffset, visibleInspo]);

  // Use a gallery image as inspiration: load its prompt (if any) + attach it as a
  // visual reference so the next generation builds on it.
  const handleUseGalleryItem = useCallback(
    (card: { thumbnail: string; prompt: string | null }) => {
      if (card.prompt) showSavedPrompt(card.prompt);
      setInspirationFiles((prev) =>
        prev.some((f) => f.url === card.thumbnail)
          ? prev
          : [
              ...prev,
              { name: "Inspiracja z galerii", url: card.thumbnail, preview: card.thumbnail },
            ]
      );
      setShowInspiration(true);
    },
    [showSavedPrompt]
  );

  const isTrialPlus = user?.trialPlus === true;

  if (isTrialPlus) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 text-center px-6">
        <div className="w-14 h-14 rounded-2xl bg-white/[0.05] border border-white/[0.1] flex items-center justify-center">
          <ImageIcon className="w-7 h-7 text-white/30" />
        </div>
        <div>
          <p className="text-white/80 font-medium text-sm">{t("trial.title")}</p>
          <p className="text-white/40 text-xs mt-1 max-w-xs">
            {t("trial.description", { trialPlusLabel: t("trial.trialPlusLabel") })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col h-full bg-[#07090e] overflow-hidden">
      {/* VP-206: jeden tracker per aktywny job — polling działa niezależnie od
          tego, którą sekcję widać (trackery nie renderują UI). */}
      {activeGenJobIds.map((jobId) => (
        <GraphicJobTracker
          key={jobId}
          jobId={jobId}
          onSnapshot={handleGenJobSnapshot}
          onPollFailure={handleGenJobPollFailure}
        />
      ))}
      {/* ─── Ambient red glow background ──────────────── */}
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div className="absolute w-[640px] h-[640px] bg-[#ef3e29]/[0.08] rounded-full filter blur-[160px] -top-40 -left-32" />
        <div className="absolute w-[520px] h-[520px] bg-[#ef3e29]/[0.06] rounded-full filter blur-[140px] -bottom-32 -right-20" />
      </div>
      <div className="relative z-10 flex flex-col h-full min-h-0">
        {/* ─── ADVANCED SECTIONS ──────────────────────────── */}
        {(!isOrganic || showStyleSection || showBrand || showInspiration || showLayout) && (
          <div className="[order:2] shrink-0 border-t border-white/[0.07] bg-transparent max-h-[45vh] overflow-y-auto">
            <div className="px-4 py-3 space-y-4">
              {/* ─── Best performing creatives from Meta — toggled from "+" popover ─── */}
              {!isOrganic && metaStatusData?.connected && (
                <div>
                  {showBestCreatives && (
                    <div className="mt-2 p-3 border border-red-500/20 rounded-xl bg-red-900/10 space-y-3">
                      <p className="text-[10px] text-white/40">{t("bestCreatives.description")}</p>

                      {/* Ad account selector */}
                      <div>
                        <Label className="text-[10px] text-white/50 mb-0.5">
                          {t("bestCreatives.adAccount")}
                        </Label>
                        <Select
                          value={bestCreativesAdAccountId}
                          onValueChange={setBestCreativesAdAccountId}
                        >
                          <SelectTrigger className="h-8 bg-[#030407] border-white/[0.1] text-white text-xs">
                            <SelectValue placeholder={t("bestCreatives.adAccountPlaceholder")} />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0a0c10] border-white/[0.1] max-h-48">
                            {adAccountsForCreatives.map((acc) => (
                              <SelectItem
                                key={acc.id}
                                value={acc.id}
                                className="text-white hover:bg-white/[0.08] text-xs"
                              >
                                {acc.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Date preset + fetch button */}
                      <div className="flex gap-2 items-end">
                        <div className="flex-1">
                          <Label className="text-[10px] text-white/50 mb-0.5">
                            {t("bestCreatives.period")}
                          </Label>
                          <Select
                            value={bestCreativesDatePreset}
                            onValueChange={setBestCreativesDatePreset}
                          >
                            <SelectTrigger className="h-8 bg-[#030407] border-white/[0.1] text-white text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0a0c10] border-white/[0.1]">
                              {DATE_PRESETS.map((dp) => (
                                <SelectItem
                                  key={dp.value}
                                  value={dp.value}
                                  className="text-white hover:bg-white/[0.08] text-xs"
                                >
                                  {dp.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          onClick={fetchBestCreatives}
                          disabled={bestCreativesFetching || !bestCreativesAdAccountId}
                          variant="outline"
                          className="h-8 text-xs border-red-500/30 text-red-300 hover:bg-red-500/10"
                        >
                          {bestCreativesFetching ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                              {t("bestCreatives.fetchingButton")}
                            </>
                          ) : (
                            <>
                              <Target className="w-3.5 h-3.5 mr-1.5" />
                              {t("bestCreatives.fetchButton")}
                            </>
                          )}
                        </Button>
                      </div>

                      {/* Results */}
                      {bestCreatives.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-[10px] text-white/50 font-medium">
                            🏆 Top {bestCreatives.length} reklam wg kosztu per lead:
                          </p>
                          <div className="grid gap-2">
                            {bestCreatives.map((creative, idx) => {
                              const previewUrl = creative.imageUrl || creative.thumbnailUrl;
                              const isExcluded = excludedBestCreativeIds.has(creative.adId);
                              return (
                                <div
                                  key={creative.adId}
                                  className={`relative flex gap-3 p-2.5 rounded-lg border transition-opacity ${isExcluded ? "border-red-500/20 bg-red-900/5 opacity-40" : "border-white/[0.08] bg-white/[0.02]"}`}
                                >
                                  {previewUrl ? (
                                    <img
                                      src={previewUrl}
                                      alt={creative.adName}
                                      className="w-20 h-20 rounded-md object-cover flex-shrink-0 border border-white/[0.08]"
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = "none";
                                      }}
                                    />
                                  ) : (
                                    <div className="w-20 h-20 rounded-md flex-shrink-0 border border-white/[0.08] bg-white/[0.04] flex items-center justify-center">
                                      <span className="text-[10px] text-white/20">Brak</span>
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-[11px] text-white/80 font-medium truncate">
                                      #{idx + 1} {creative.headline || creative.adName}
                                    </p>
                                    <p className="text-[9px] text-white/40 truncate mt-0.5">
                                      {creative.primaryText?.slice(0, 100) || "—"}
                                    </p>
                                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5">
                                      {creative.leads > 0 && (
                                        <span className="text-[10px] font-semibold text-red-400">
                                          {t("bestCreatives.metrics.cpl", {
                                            value: creative.costPerLead.toFixed(2),
                                            currency: t("bestCreatives.currency"),
                                          })}
                                        </span>
                                      )}
                                      {creative.leads > 0 && (
                                        <span className="text-[10px] text-red-400/70">
                                          {t("bestCreatives.metrics.leads", {
                                            count: creative.leads,
                                          })}
                                        </span>
                                      )}
                                      <span className="text-[9px] text-emerald-400">
                                        {t("bestCreatives.metrics.ctr", {
                                          value: parseFloat(creative.ctr).toFixed(2),
                                        })}
                                      </span>
                                      <span className="text-[9px] text-white/40">
                                        {t("bestCreatives.metrics.spent", {
                                          value: parseFloat(creative.spend).toFixed(0),
                                          currency: t("bestCreatives.currency"),
                                        })}
                                      </span>
                                      <span className="text-[9px] text-white/40">
                                        {t("bestCreatives.metrics.clicks", {
                                          count: creative.clicks,
                                        })}
                                      </span>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    title={
                                      isExcluded
                                        ? t("bestCreatives.restoreTitle")
                                        : t("bestCreatives.excludeTitle")
                                    }
                                    onClick={() =>
                                      setExcludedBestCreativeIds((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(creative.adId)) {
                                          next.delete(creative.adId);
                                        } else {
                                          next.add(creative.adId);
                                        }
                                        return next;
                                      })
                                    }
                                    className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center transition-colors ${isExcluded ? "bg-red-500/30 text-red-400 hover:bg-red-500/50" : "bg-white/[0.06] text-white/30 hover:bg-red-500/20 hover:text-red-400"}`}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>

                          {/* Use as inspiration button */}
                          <Button
                            onClick={handleUseCreativesAsInspiration}
                            disabled={bestCreatives.every((c) =>
                              excludedBestCreativeIds.has(c.adId)
                            )}
                            className="w-full h-9 text-xs bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                            {t("bestCreatives.useButton", {
                              count: bestCreatives.filter(
                                (c) => !excludedBestCreativeIds.has(c.adId)
                              ).length,
                            })}
                          </Button>
                        </div>
                      )}

                      {!bestCreativesFetching &&
                        bestCreatives.length === 0 &&
                        bestCreativesAdAccountId && (
                          <p className="text-[10px] text-white/30 text-center py-2">
                            {t("bestCreatives.emptyHint")}
                          </p>
                        )}
                    </div>
                  )}
                </div>
              )}

              {!isOrganic && <></>}

              {/* ─── Style Selection (optional, collapsible) ───── */}
              {showStyleSection && (
                <div>
                  <p className="text-xs font-medium text-white/60 mb-1.5">
                    {t("popoverMenu.style")}
                  </p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {STYLES.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setStyle(style === s.id ? null : s.id)}
                        className={`p-2 rounded-xl border text-left transition-all ${
                          style === s.id
                            ? "bg-[#ef3e29]/10 border-[#ef3e29]/45 shadow-[0_0_18px_rgba(239,62,41,0.18)]"
                            : "bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04] hover:border-[#ef3e29]/25"
                        }`}
                      >
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs">{s.emoji}</span>
                          <span
                            className={`text-[11px] font-medium ${style === s.id ? "text-white" : "text-white/80"}`}
                          >
                            {t(s.label)}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ─── Product section ──────────────────────────── */}
              {!isOrganic && showProduct && (
                <div className="p-2.5 border border-white/[0.08] rounded-xl bg-white/[0.02] space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] text-white/50 mb-0.5">
                        {t("product.nameLabel")}
                      </Label>
                      <Input
                        value={productName}
                        onChange={(e) => setProductName(e.target.value)}
                        placeholder="np. Kurs marketingu"
                        className="h-7 bg-[#030407] border-white/[0.1] text-white text-xs placeholder:text-white/30"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] text-white/50 mb-0.5">
                        {t("product.priceLabel")}
                      </Label>
                      <Input
                        value={productPrice}
                        onChange={(e) => setProductPrice(e.target.value)}
                        placeholder="np. 297 PLN"
                        className="h-7 bg-[#030407] border-white/[0.1] text-white text-xs placeholder:text-white/30"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ─── Brand section ────────────────────────────── */}
              {showBrand && (
                <div className="p-2.5 border border-white/[0.08] rounded-xl bg-white/[0.02] space-y-2">
                  <div className="flex items-center justify-between border-b border-white/[0.06] pb-1.5 mb-1">
                    <h3 className="text-sm font-semibold text-white flex items-center gap-1.5">
                      🖼️ Avatar / Marka
                      <SectionInfoBubble
                        title="Avatar / Marka"
                        description="Avatar = persona (Mateusz, Pamela, Brand VP) z kontekstem (jak się komunikujesz, słowa, frazy) i opcjonalnie zdjęciem. Jeden klik podpina cały kontekst do generatora — nie musisz wypełniać 5 pól za każdym razem."
                        example="Avatar 'Mateusz CEO' → kontekst: 'Mówię bezpośrednio, używam slangu, nie boję się przekleństw'. Każda grafika będzie w tym tonie."
                      />
                    </h3>
                    <span className="text-[9px] text-white/40">Persona + zdjęcia + voice refs</span>
                  </div>
                  <AvatarSelector
                    onContextChange={(ctx) => {
                      if (ctx && !brandName) setBrandName(ctx.split("\n")[0]?.slice(0, 40) ?? "");
                    }}
                  />
                  <div className="border-t border-white/[0.06] pt-2">
                    <Label className="text-[10px] text-white/50 mb-0.5">
                      {t("brand.nameLabel")}
                    </Label>
                    <Input
                      value={brandName}
                      onChange={(e) => setBrandName(e.target.value)}
                      placeholder="np. Viral Partners"
                      className="h-7 bg-[#030407] border-white/[0.1] text-white text-xs placeholder:text-white/30"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-white/50 mb-0.5">
                      {t("brand.ctaLabel")}
                    </Label>
                    <Input
                      value={ctaText}
                      onChange={(e) => setCtaText(e.target.value)}
                      placeholder={t("brand.ctaPlaceholder")}
                      className="h-7 bg-[#030407] border-white/[0.1] text-white text-xs placeholder:text-white/30"
                    />
                    <div className="flex flex-wrap gap-1 mt-1">
                      {CTA_PRESETS.map((cta) => (
                        <button
                          key={cta}
                          onClick={() => setCtaText(cta)}
                          className={`px-2 py-0.5 text-[9px] rounded-md border transition-all ${ctaText === cta ? "bg-[#ef3e29]/10 border-[#ef3e29]/40 text-white" : "bg-white/[0.02] border-white/[0.06] text-white/45 hover:text-white/70 hover:border-[#ef3e29]/20"}`}
                        >
                          {cta}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Brand book upload — DEPRECATED: hidden, replaced by Avatar context (transcript 2026-05). Kept for rollback. */}
                  {SHOW_BRAND_BOOK_UPLOAD && (
                    <div>
                      <Label className="text-[10px] text-white/50 mb-0.5">Brand book (PDF)</Label>
                      <input
                        ref={brandBookInputRef}
                        type="file"
                        accept=".pdf,image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={handleBrandBookUpload}
                      />
                      {brandBookFileName ? (
                        <div className="flex items-center gap-2 p-1.5 rounded-lg border border-emerald-500/20 bg-emerald-600/10">
                          <FileText className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                          <span className="text-[10px] text-emerald-300 truncate flex-1">
                            {brandBookFileName}
                          </span>
                          {brandBookText && (
                            <span className="text-[9px] text-emerald-400/60 flex-shrink-0">
                              {brandBookText.length} zn.
                            </span>
                          )}
                          <button
                            onClick={() => {
                              setBrandBookFileName("");
                              setBrandBookText("");
                            }}
                            className="p-0.5 rounded hover:bg-white/10 text-white/40 hover:text-white transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => brandBookInputRef.current?.click()}
                          disabled={brandBookUploading}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-dashed border-white/[0.12] text-[10px] text-white/40 hover:text-white/60 hover:border-white/[0.2] transition-all w-full"
                        >
                          {brandBookUploading ? (
                            <>
                              <Loader2 className="w-3 h-3 animate-spin" />{" "}
                              {t("brand.brandBookUploading")}
                            </>
                          ) : (
                            <>
                              <Upload className="w-3 h-3" /> {t("brand.brandBookButton")}
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  )}

                  {/* ── Brand Assets (pliki brandowe) ── */}
                  <div className="border-t border-white/[0.06] pt-2">
                    <div className="flex items-center justify-between mb-1.5">
                      <Label className="text-[10px] text-white/50 flex items-center gap-1">
                        <ImagePlus className="w-3 h-3 text-red-400" /> {t("brand.assetsLabel")}
                        {brandAssets.length > 0 && (
                          <span className="text-[9px] text-red-400/60">({brandAssets.length})</span>
                        )}
                      </Label>
                      {agencyClients.length > 0 && !selectedClientId && (
                        <span className="text-[9px] text-white/30 italic mr-1">
                          {t("brand.noClient")}
                        </span>
                      )}
                      <button
                        onClick={() => setAssetUploadOpen(!assetUploadOpen)}
                        className="text-[9px] text-red-400 hover:text-red-300 transition-colors flex items-center gap-0.5"
                      >
                        <Plus className="w-3 h-3" /> {t("brand.addAsset")}
                      </button>
                    </div>

                    {/* Upload form */}
                    {assetUploadOpen && (
                      <div className="p-2 rounded-lg border border-red-500/20 bg-red-900/10 space-y-2 mb-2">
                        <input
                          ref={assetInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/svg+xml"
                          className="hidden"
                          onChange={handleAssetFileSelect}
                        />
                        {assetPreview ? (
                          <div className="relative w-full h-24 rounded-lg overflow-hidden border border-white/[0.1]">
                            <img
                              src={assetPreview}
                              alt="preview"
                              className="w-full h-full object-contain bg-black/50"
                            />
                            <button
                              onClick={() => {
                                setAssetFile(null);
                                setAssetPreview(null);
                              }}
                              className="absolute top-1 right-1 p-0.5 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => assetInputRef.current?.click()}
                            className="flex items-center justify-center gap-1.5 w-full h-16 rounded-lg border-2 border-dashed border-red-500/20 text-[10px] text-white/40 hover:text-white/60 hover:border-red-500/40 transition-all"
                          >
                            <Upload className="w-4 h-4" /> {t("assetUpload.selectFile")}
                          </button>
                        )}
                        <Input
                          value={assetName}
                          onChange={(e) => setAssetName(e.target.value)}
                          placeholder={t("assetUpload.namePlaceholder")}
                          className="h-7 bg-[#030407] border-white/[0.1] text-white text-xs placeholder:text-white/30"
                          maxLength={200}
                        />
                        <Textarea
                          value={assetDescription}
                          onChange={(e) => setAssetDescription(e.target.value)}
                          placeholder={t("assetUpload.descPlaceholder")}
                          className="min-h-[50px] bg-[#030407] border-white/[0.1] text-white text-xs placeholder:text-white/30 resize-none"
                          maxLength={2000}
                        />
                        <Select value={assetFileType} onValueChange={setAssetFileType}>
                          <SelectTrigger className="h-7 bg-[#030407] border-white/[0.1] text-white text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0a0c10] border-white/[0.1]">
                            <SelectItem value="logo" className="text-white text-xs">
                              {t("assetUpload.logo")}
                            </SelectItem>
                            <SelectItem value="person" className="text-white text-xs">
                              {t("assetUpload.person")}
                            </SelectItem>
                            <SelectItem value="scene" className="text-white text-xs">
                              {t("assetUpload.scene")}
                            </SelectItem>
                            <SelectItem value="graphic" className="text-white text-xs">
                              {t("assetUpload.graphic")}
                            </SelectItem>
                            <SelectItem value="other" className="text-white text-xs">
                              {t("assetUpload.other")}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={handleAssetUpload}
                            disabled={
                              assetUploading ||
                              !assetFile ||
                              !assetName.trim() ||
                              !assetDescription.trim()
                            }
                            className="flex-1 h-7 text-[10px] bg-red-600 hover:bg-red-700 text-white"
                          >
                            {assetUploading ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <Upload className="w-3 h-3 mr-1" />
                            )}
                            {t("assetUpload.uploadButton")}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setAssetUploadOpen(false);
                              setAssetFile(null);
                              setAssetPreview(null);
                              setAssetName("");
                              setAssetDescription("");
                            }}
                            className="h-7 text-[10px] text-white/40 hover:text-white"
                          >
                            {t("assetUpload.cancelButton")}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Assets gallery */}
                    {brandAssets.length > 0 && (
                      <div className="grid grid-cols-3 gap-1.5">
                        {brandAssets.map((asset) => {
                          const isSelected = selectedAssetIds.has(asset.id);
                          return (
                            <div
                              key={asset.id}
                              className={`relative rounded-lg overflow-hidden border cursor-pointer group transition-all ${
                                isSelected
                                  ? "border-red-500/60 ring-1 ring-red-500/30 bg-red-900/10"
                                  : "border-white/[0.08] hover:border-white/[0.2]"
                              }`}
                              onClick={() => toggleAssetSelection(asset.id)}
                            >
                              <div className="w-full aspect-square bg-black/40 flex items-center justify-center">
                                <img
                                  src={asset.fileUrl}
                                  alt={asset.name}
                                  className="max-w-full max-h-full object-contain"
                                />
                              </div>
                              <div className="p-1">
                                <p className="text-[8px] text-white/70 truncate font-medium">
                                  {asset.name}
                                </p>
                                <p className="text-[7px] text-white/40 truncate">
                                  {asset.description.slice(0, 50)}
                                </p>
                              </div>
                              {isSelected && (
                                <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                                  <Check className="w-2.5 h-2.5 text-white" />
                                </div>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAssetDelete(asset.id);
                                }}
                                className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-black/60 text-white/40 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                              >
                                <Trash2 className="w-2.5 h-2.5" />
                              </button>
                              <span className="absolute bottom-0.5 right-0.5 text-[7px] px-1 py-0.5 rounded bg-black/40 text-white/50">
                                {asset.fileType === "logo"
                                  ? "🏷️"
                                  : asset.fileType === "person" || asset.fileType === "photo"
                                    ? "👤"
                                    : asset.fileType === "scene"
                                      ? "🏙️"
                                      : asset.fileType === "graphic"
                                        ? "🎨"
                                        : "📁"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {brandAssets.length > 0 &&
                      selectedAssetIds.size > 0 &&
                      (() => {
                        const hasSelectedPhoto = brandAssets.some(
                          (a) =>
                            selectedAssetIds.has(a.id) &&
                            (a.fileType === "photo" || a.fileType === "person")
                        );
                        const hasInspirationImages = inspirationFiles.length > 0;
                        const personSwapActive =
                          hasSelectedPhoto &&
                          hasInspirationImages &&
                          [
                            "nano-banana-pro",
                            "nano-banana-2",
                            "gpt-image-2",
                            "seedream-4.5",
                            "seedream-5.0-lite",
                            "seedream-4.0",
                          ].includes(imageModel);
                        const hasScene = brandAssets.some(
                          (a) => selectedAssetIds.has(a.id) && a.fileType === "scene"
                        );
                        return (
                          <div className="mt-1 space-y-0.5">
                            <p className="text-[9px] text-red-400/70">
                              ✓ {selectedAssetIds.size}{" "}
                              {selectedAssetIds.size === 1
                                ? t("assetUpload.selectedCount_one", {
                                    count: selectedAssetIds.size,
                                  })
                                : t("assetUpload.selectedCount_other", {
                                    count: selectedAssetIds.size,
                                  })}
                            </p>
                            {personSwapActive && (
                              <p className="text-[9px] text-orange-400/80 flex items-center gap-1">
                                🔄{" "}
                                {hasScene
                                  ? t("assetUpload.sceneSwap")
                                  : t("assetUpload.personSwap")}
                              </p>
                            )}
                          </div>
                        );
                      })()}
                  </div>

                  {/* ── Szablony graficzne (schematy) ── */}
                  <div className="border-t border-white/[0.06] pt-2">
                    <BrandTemplateEditor selectedClientId={selectedClientId || undefined} />
                  </div>
                </div>
              )}

              {/* ─── Visual inspiration section ───────────────── */}
              {showInspiration && (
                <div className="p-2.5 border border-white/[0.08] rounded-xl bg-white/[0.02] space-y-2">
                  <input
                    ref={inspirationInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleInspirationUpload}
                  />
                  <div className="flex items-center justify-between border-b border-white/[0.06] pb-1.5 mb-1">
                    <h3 className="text-sm font-semibold text-white flex items-center gap-1.5">
                      {t("inspiration.sectionHeader")}
                      <SectionInfoBubble
                        title="Inspiracja wizualna"
                        description="Wklej link do reklamy / posta / tablicy Pinterest, dodaj zdjęcie albo wpisz adres profilu Instagram. AI użyje tego jako wzorca stylu — nie kopiuje 1:1, tylko czerpie inspirację (kompozycja, kolory, mood)."
                        example="Link do tablicy Pinterest 'minimalist coffee branding' → AI generuje grafikę w podobnym stylu, ale z Twoim produktem."
                      />
                    </h3>
                    <span className="text-[9px] text-white/40">{t("inspiration.sectionHint")}</span>
                  </div>
                  {/* ── Twoja pełna biblioteka — WSZYSTKIE zdjęcia user-a z firm-assets + UGC avatary + HeyGen + media uploads. */}
                  {allMedia.length > 0 && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-[0.15em] text-white/40">
                          📁 Twoja biblioteka ({allMedia.length})
                        </span>
                        <div className="flex gap-1 flex-wrap">
                          <button
                            onClick={() => setMediaFilter("all")}
                            className={`text-[9px] px-1.5 py-0.5 rounded ${mediaFilter === "all" ? "bg-white/15 text-white" : "bg-white/[0.03] text-white/40 hover:text-white/60"}`}
                          >
                            Wszystko
                          </button>
                          <button
                            onClick={() => setMediaFilter("logo")}
                            className={`text-[9px] px-1.5 py-0.5 rounded ${mediaFilter === "logo" ? "bg-white/15 text-white" : "bg-white/[0.03] text-white/40 hover:text-white/60"}`}
                          >
                            Logo
                          </button>
                          <button
                            onClick={() => setMediaFilter("face")}
                            className={`text-[9px] px-1.5 py-0.5 rounded ${mediaFilter === "face" ? "bg-white/15 text-white" : "bg-white/[0.03] text-white/40 hover:text-white/60"}`}
                          >
                            Twarz
                          </button>
                          <button
                            onClick={() => setMediaFilter("product")}
                            className={`text-[9px] px-1.5 py-0.5 rounded ${mediaFilter === "product" ? "bg-white/15 text-white" : "bg-white/[0.03] text-white/40 hover:text-white/60"}`}
                          >
                            Produkt
                          </button>
                          <button
                            onClick={() => setMediaFilter("scene")}
                            className={`text-[9px] px-1.5 py-0.5 rounded ${mediaFilter === "scene" ? "bg-white/15 text-white" : "bg-white/[0.03] text-white/40 hover:text-white/60"}`}
                          >
                            Scena
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5">
                        {allMedia
                          .filter((m) => m.mediaType === "image")
                          .filter((m) =>
                            mediaFilter === "all" ? true : m.category === mediaFilter
                          )
                          .map((m) => {
                            const isSelected = inspirationFiles.some(
                              (f) => f.url === m.fileUrl || f.preview === m.fileUrl
                            );
                            return (
                              <button
                                key={m.uniqueKey}
                                type="button"
                                onClick={() => {
                                  if (isSelected) {
                                    setInspirationFiles((prev) =>
                                      prev.filter(
                                        (f) => f.url !== m.fileUrl && f.preview !== m.fileUrl
                                      )
                                    );
                                  } else if (inspirationFiles.length >= MAX_INSPIRATION_FILES) {
                                    toast({
                                      title: t("inspiration.limitTitle"),
                                      description: t("inspiration.limitDesc", {
                                        max: MAX_INSPIRATION_FILES,
                                      }),
                                      variant: "destructive",
                                    });
                                  } else {
                                    setInspirationFiles((prev) => [
                                      ...prev,
                                      { name: m.title, url: m.fileUrl, preview: m.fileUrl },
                                    ]);
                                  }
                                }}
                                title={`${m.title} • ${CATEGORY_LABELS[m.category]} • ${SOURCE_LABELS[m.source]}`}
                                className={`relative aspect-square rounded-md overflow-hidden border-2 transition ${isSelected ? "border-emerald-500 ring-1 ring-emerald-500/50" : "border-white/10 hover:border-white/30"}`}
                              >
                                <img
                                  src={m.thumbnailUrl ?? m.fileUrl}
                                  alt={m.title}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                                <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-[8px] text-white/90 px-1 py-0.5 truncate text-left">
                                  {CATEGORY_LABELS[m.category]}
                                </span>
                                {isSelected && (
                                  <div className="absolute top-0.5 right-0.5 bg-emerald-500 rounded-full p-0.5">
                                    <Check className="w-2.5 h-2.5 text-white" />
                                  </div>
                                )}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  )}
                  {/* Source picker — single click jumps to relevant input */}
                  <div className="grid grid-cols-4 gap-1.5">
                    <button
                      type="button"
                      onClick={() => setInspirationSource("link")}
                      className={`px-2 py-1.5 rounded-lg border text-[10px] font-medium transition-all flex items-center justify-center gap-1 ${
                        inspirationSource === "link"
                          ? "bg-blue-600/15 border-blue-500/45 text-blue-200"
                          : "bg-white/[0.03] border-white/[0.08] text-white/55 hover:text-white hover:border-white/[0.15]"
                      }`}
                    >
                      <Link2 className="w-3 h-3" /> Link
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setInspirationSource("upload");
                        inspirationInputRef.current?.click();
                      }}
                      className={`px-2 py-1.5 rounded-lg border text-[10px] font-medium transition-all flex items-center justify-center gap-1 ${
                        inspirationSource === "upload"
                          ? "bg-emerald-600/15 border-emerald-500/45 text-emerald-200"
                          : "bg-white/[0.03] border-white/[0.08] text-white/55 hover:text-white hover:border-white/[0.15]"
                      }`}
                    >
                      <ImageIcon className="w-3 h-3" /> Grafika
                    </button>
                    <button
                      type="button"
                      onClick={() => setInspirationSource("pinterest")}
                      className={`px-2 py-1.5 rounded-lg border text-[10px] font-medium transition-all flex items-center justify-center gap-1 ${
                        inspirationSource === "pinterest"
                          ? "bg-pink-600/15 border-pink-500/45 text-pink-200"
                          : "bg-white/[0.03] border-white/[0.08] text-white/55 hover:text-white hover:border-white/[0.15]"
                      }`}
                    >
                      <span className="text-[11px]">📌</span> Pinterest
                    </button>
                    <button
                      type="button"
                      onClick={() => setInspirationSource("instagram")}
                      className={`px-2 py-1.5 rounded-lg border text-[10px] font-medium transition-all flex items-center justify-center gap-1 ${
                        inspirationSource === "instagram"
                          ? "bg-fuchsia-600/15 border-fuchsia-500/45 text-fuchsia-200"
                          : "bg-white/[0.03] border-white/[0.08] text-white/55 hover:text-white hover:border-white/[0.15]"
                      }`}
                    >
                      <span className="text-[11px]">📷</span> Instagram
                    </button>
                  </div>
                  <div>
                    <Label className="text-[10px] text-white/50 mb-1">
                      {t("inspiration.apiModeLabel")}
                    </Label>
                    <div className="grid grid-cols-3 gap-1.5">
                      {GENERATION_MODES.map((mode) => (
                        <button
                          key={mode.id}
                          onClick={() => setGenerationMode(mode.id)}
                          className={`p-1.5 rounded-lg border text-left transition-all ${
                            generationMode === mode.id
                              ? "bg-red-600/20 border-red-500/50 text-red-200"
                              : "bg-white/[0.03] border-white/[0.08] text-white/60 hover:text-white hover:border-white/[0.15]"
                          }`}
                          title={mode.desc}
                        >
                          <div className="text-[10px] font-semibold">{mode.label}</div>
                          <div className="text-[9px] text-white/45 leading-tight mt-0.5">
                            {t(mode.desc)}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* URL input for Instagram / Facebook / Ad Library links */}
                  {(inspirationSource === "link" || inspirationSource === "instagram") && (
                    <div>
                      <Label className="text-[10px] text-white/50 mb-1">
                        {t("inspiration.linkLabel")}
                      </Label>
                      <div className="flex gap-1.5">
                        <Input
                          value={inspirationUrl}
                          onChange={(e) => setInspirationUrl(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleInspirationUrlFetch();
                            }
                          }}
                          placeholder={
                            inspirationSource === "instagram"
                              ? "https://instagram.com/p/..."
                              : "Link do posta z Instagrama, Facebooka lub Ad Library..."
                          }
                          className="flex-1 h-8 bg-[#030407] border-white/[0.1] text-white text-xs placeholder:text-white/30"
                          disabled={inspirationUrlLoading}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleInspirationUrlFetch}
                          disabled={!inspirationUrl.trim() || inspirationUrlLoading}
                          className="h-8 px-2.5 border-white/[0.12] bg-white/[0.04] text-white/70 hover:text-white hover:bg-white/[0.08]"
                        >
                          {inspirationUrlLoading ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Link2 className="w-3.5 h-3.5" />
                          )}
                          <span className="ml-1 text-[10px]">{t("inspiration.fetchButton")}</span>
                        </Button>
                      </div>
                      <p className="text-[9px] text-white/30 mt-0.5">{t("inspiration.linkHint")}</p>
                    </div>
                  )}
                  {/* Pinterest board input */}
                  {inspirationSource === "pinterest" && (
                    <div className="border-t border-white/[0.06] pt-2">
                      <Label className="text-[10px] text-white/50 mb-1 flex items-center gap-1">
                        <span className="text-pink-400">📌</span> {t("inspiration.pinterestLabel")}
                      </Label>
                      <div className="flex gap-1.5">
                        <Input
                          value={pinterestUrl}
                          onChange={(e) => setPinterestUrl(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handlePinterestBoardFetch();
                            }
                          }}
                          placeholder="https://pl.pinterest.com/user/board-name..."
                          className="flex-1 h-8 bg-[#030407] border-white/[0.1] text-white text-xs placeholder:text-white/30"
                          disabled={pinterestLoading}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handlePinterestBoardFetch}
                          disabled={!pinterestUrl.trim() || pinterestLoading}
                          className="h-8 px-2.5 border-pink-500/20 bg-pink-600/10 text-pink-300 hover:text-pink-200 hover:bg-pink-600/20"
                        >
                          {pinterestLoading ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Link2 className="w-3.5 h-3.5" />
                          )}
                          <span className="ml-1 text-[10px]">
                            {t("inspiration.pinterestFetch")}
                          </span>
                        </Button>
                      </div>
                      <p className="text-[9px] text-white/30 mt-0.5">
                        {t("inspiration.pinterestHint")}
                      </p>
                    </div>
                  )}
                  {/* Pinterest preview grid */}
                  {pinterestPreviews.length > 0 && (
                    <div className="border border-pink-500/20 rounded-xl bg-pink-900/5 p-2.5 space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-[10px] text-pink-300 font-medium">
                          {t("inspiration.pinterestSelectionLabel", {
                            selected: pinterestPreviews.filter((p) => p.selected).length,
                            total: pinterestPreviews.length,
                          })}
                        </Label>
                        <div className="flex gap-1.5">
                          <button
                            onClick={() =>
                              setPinterestPreviews((prev) =>
                                prev.map((p) => ({ ...p, selected: true }))
                              )
                            }
                            className="text-[9px] text-pink-400/70 hover:text-pink-300 transition-colors"
                          >
                            {t("inspiration.pinterestSelectAll")}
                          </button>
                          <button
                            onClick={() =>
                              setPinterestPreviews((prev) =>
                                prev.map((p) => ({ ...p, selected: false }))
                              )
                            }
                            className="text-[9px] text-white/30 hover:text-white/50 transition-colors"
                          >
                            {t("inspiration.pinterestDeselectAll")}
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-1.5">
                        {pinterestPreviews.map((pin, idx) => (
                          <div
                            key={idx}
                            onClick={() => handlePinterestTogglePin(idx)}
                            className={`relative rounded-lg overflow-hidden border cursor-pointer transition-all ${
                              pin.selected
                                ? "border-pink-500/60 ring-1 ring-pink-500/30"
                                : "border-white/[0.08] opacity-40 hover:opacity-70"
                            }`}
                          >
                            <img
                              src={pin.imageUrl}
                              alt={`Pin ${idx + 1}`}
                              className="w-full h-20 object-cover"
                            />
                            {pin.selected && (
                              <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-pink-500 flex items-center justify-center">
                                <Check className="w-2.5 h-2.5 text-white" />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <Button
                        size="sm"
                        onClick={handlePinterestConfirm}
                        disabled={pinterestPreviews.filter((p) => p.selected).length === 0}
                        className="w-full h-8 text-[11px] bg-pink-600 hover:bg-pink-700 text-white"
                      >
                        {t("inspiration.pinterestConfirm", {
                          count: pinterestPreviews.filter((p) => p.selected).length,
                        })}
                      </Button>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {inspirationFiles.map((file, idx) => (
                      <div
                        key={idx}
                        className="relative w-16 h-16 rounded-lg overflow-hidden border border-white/[0.1] group"
                      >
                        <img
                          src={file.preview || file.url}
                          alt={file.name}
                          className="w-full h-full object-cover"
                        />
                        {file.uploading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                            <Loader2 className="w-4 h-4 text-white animate-spin" />
                          </div>
                        )}
                        <button
                          onClick={() => removeInspirationFile(idx)}
                          className="absolute top-0.5 right-0.5 p-0.5 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => inspirationInputRef.current?.click()}
                      className="w-16 h-16 rounded-lg border-2 border-dashed border-white/[0.12] flex flex-col items-center justify-center gap-0.5 text-white/40 hover:text-white/60 hover:border-white/[0.2] transition-all"
                    >
                      <Upload className="w-4 h-4" />
                      <span className="text-[8px]">{t("inspiration.uploadButton")}</span>
                    </button>
                  </div>
                  <Textarea
                    value={inspirationNote}
                    onChange={(e) => setInspirationNote(e.target.value)}
                    placeholder="np. Styl jak reklamy Apple — minimalizm..."
                    className="min-h-[40px] bg-[#030407] border-white/[0.1] text-white text-xs placeholder:text-white/30 resize-none"
                    maxLength={1000}
                  />
                </div>
              )}

              {/* ─── Layout templates section ─────────────────── */}
              {showLayout && (
                <div className="p-2.5 border border-white/[0.08] rounded-xl bg-white/[0.02] space-y-2">
                  <div className="flex items-center justify-between border-b border-white/[0.06] pb-1.5 mb-1">
                    <h3 className="text-sm font-semibold text-white flex items-center gap-1.5">
                      📐 Układ / szablony
                      <SectionInfoBubble
                        title="Układ grafiki"
                        description="Gotowe schematy kompozycji: gdzie idzie nagłówek, gdzie produkt, gdzie CTA. Wybór układu zawęża styl do konkretnego formatu (np. miniatura YouTube, post Instagram). Bez układu AI sam decyduje o kompozycji."
                        example="Wybór 'YouTube thumbnail' → grafika 16:9, duży tekst u dołu, twarz i emocja w środku."
                      />
                    </h3>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {LAYOUT_TEMPLATES.map((layout) => (
                      <button
                        key={layout.id}
                        onClick={() => {
                          const next = selectedLayout === layout.id ? null : layout.id;
                          setSelectedLayout(next);
                          setSelectedExampleId(null);
                          if (next === "youtube-thumbnail") {
                            setSelectedFormats(["landscape"]);
                          }
                        }}
                        className={`p-2 rounded-lg border text-left transition-all ${selectedLayout === layout.id ? "bg-red-600/15 border-red-500/40 ring-1 ring-red-500/20" : "bg-white/[0.03] border-white/[0.08] hover:border-white/[0.15]"}`}
                      >
                        <div
                          className={`text-[11px] font-medium mb-0.5 ${selectedLayout === layout.id ? "text-red-300" : "text-white/80"}`}
                        >
                          {t(layout.label)}
                        </div>
                        <p className="text-[9px] text-white/40 leading-tight">{t(layout.desc)}</p>
                      </button>
                    ))}
                  </div>
                  {(() => {
                    const layoutExamples = selectedLayout
                      ? EXAMPLE_TEMPLATES.filter((ex) => ex.layout === selectedLayout)
                      : [];
                    if (layoutExamples.length === 0) return null;
                    return (
                      <div className="pt-2 border-t border-white/[0.06]">
                        <p className="text-[10px] text-white/40 mb-1.5">
                          {t("copyPreview.examplesFor")}
                        </p>
                        <div className="flex gap-2 overflow-x-auto pb-1">
                          {layoutExamples.map((ex) => (
                            <div
                              key={ex.id}
                              className={`flex-shrink-0 w-28 rounded-lg border overflow-hidden transition-all ${selectedExampleId === ex.id ? "border-red-500/60 ring-1 ring-red-500/30 bg-red-600/10" : "border-white/[0.08] bg-white/[0.03]"}`}
                            >
                              <img
                                src={ex.thumbnail}
                                alt={ex.label}
                                className="w-full h-20 object-cover"
                              />
                              <div className="p-1.5 space-y-1">
                                <p className="text-[10px] text-white/60 truncate">{ex.label}</p>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => setPreviewExample(ex)}
                                    className="flex-1 flex items-center justify-center gap-0.5 px-1 py-0.5 rounded text-[9px] bg-white/[0.06] hover:bg-white/[0.12] text-white/50 hover:text-white transition-colors"
                                    title={t("popoverMenu.preview")}
                                  >
                                    <ZoomIn className="w-2.5 h-2.5" />
                                  </button>
                                  <button
                                    onClick={() => handleUseExample(ex)}
                                    className={`flex-1 flex items-center justify-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-medium transition-colors ${selectedExampleId === ex.id ? "bg-red-600/40 text-red-200" : "bg-red-600/20 hover:bg-red-600/30 text-red-300"}`}
                                    title={t("popoverMenu.useExample")}
                                  >
                                    <Sparkles className="w-2.5 h-2.5" />{" "}
                                    {selectedExampleId === ex.id
                                      ? t("popoverMenu.selected")
                                      : t("popoverMenu.use")}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* ─── Copy Preview Panel ──────────────────────── */}
              {copyPreview.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-amber-400" />
                      <span className="text-sm font-semibold text-white">
                        {t("copyPreview.title", { count: copyPreview.length })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => setCopyPreview([])}
                        className="text-xs bg-white/[0.06] text-white/50 hover:text-white hover:bg-white/10 border border-white/[0.08]"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        {t("copyPreview.regenerate")}
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleGenerate}
                        disabled={generating}
                        className="text-xs bg-red-600/80 hover:bg-red-600 text-white"
                      >
                        <Sparkles className="w-3 h-3 mr-1" />
                        {t("copyPreview.generate")}
                      </Button>
                    </div>
                  </div>
                  <p className="text-[10px] text-white/30">{t("copyPreview.hint")}</p>
                  <div className="grid grid-cols-1 gap-2">
                    {copyPreview.map((variant, i) => (
                      <div
                        key={i}
                        className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.08] space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-white/40 font-medium">
                            {t("copyPreview.variantLabel", { index: i + 1 })}
                          </span>
                          <button
                            onClick={() => setCopyPreview((prev) => prev.filter((_, j) => j !== i))}
                            className="p-1 rounded hover:bg-white/10 text-white/30 hover:text-red-400 transition-colors"
                            title={t("copyPreview.deleteVariant")}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <div>
                          <label className="text-[10px] text-white/30 mb-0.5 block">
                            {t("copyPreview.headlineLabel")}
                          </label>
                          <input
                            type="text"
                            value={variant.headline}
                            onChange={(e) =>
                              setCopyPreview((prev) =>
                                prev.map((v, j) =>
                                  j === i ? { ...v, headline: e.target.value } : v
                                )
                              )
                            }
                            className="w-full h-7 px-2 text-xs rounded bg-[#030407] border border-white/[0.1] text-white focus:outline-none focus:border-white/20"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-white/30 mb-0.5 block">
                            {t("copyPreview.primaryTextLabel")}
                          </label>
                          <input
                            type="text"
                            value={variant.primaryText}
                            onChange={(e) =>
                              setCopyPreview((prev) =>
                                prev.map((v, j) =>
                                  j === i ? { ...v, primaryText: e.target.value } : v
                                )
                              )
                            }
                            className="w-full h-7 px-2 text-xs rounded bg-[#030407] border border-white/[0.1] text-white focus:outline-none focus:border-white/20"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-white/30 mb-0.5 block">
                            {t("copyPreview.ctaLabel")}
                          </label>
                          <input
                            type="text"
                            value={variant.ctaText}
                            onChange={(e) =>
                              setCopyPreview((prev) =>
                                prev.map((v, j) =>
                                  j === i ? { ...v, ctaText: e.target.value } : v
                                )
                              )
                            }
                            className="w-full h-7 px-2 text-xs rounded bg-[#030407] border border-white/[0.1] text-white focus:outline-none focus:border-white/20"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── BOTTOM TOOLBAR ──────────────────────────────────── */}
        <div className="[order:3] shrink-0 border-t border-white/[0.06] bg-transparent px-6 py-3">
          {generateMutation.isError && (
            <div className="flex items-start gap-2 p-2 mb-2 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
              <span className="text-xs text-red-300 leading-relaxed">
                {(generateMutation.error as Error)?.message || t("toolbar.error")}
              </span>
            </div>
          )}
          {/* ─── Mode toggle: Lead Gen ↔ E-commerce ─── */}
          {!isOrganic && (
            <div className="mb-3 flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06]">
              <div className="inline-flex p-0.5 rounded-lg bg-black/40 border border-white/[0.06]">
                <button
                  type="button"
                  onClick={() => setIsEcommerceMode(false)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    !isEcommerceMode
                      ? "bg-[#ef3e29]/15 text-white shadow-[0_0_12px_rgba(239,62,41,0.2)]"
                      : "text-white/50 hover:text-white/80"
                  }`}
                >
                  {t("modeToggle.leadGen")}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEcommerceMode(true)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    isEcommerceMode
                      ? "bg-orange-500/15 text-white shadow-[0_0_12px_rgba(249,115,22,0.25)]"
                      : "text-white/50 hover:text-white/80"
                  }`}
                >
                  🛒 {t("modeToggle.ecommerce")}
                </button>
              </div>
              <span className="text-[11px] text-white/45 leading-snug">
                {isEcommerceMode ? t("modeToggle.ecomHint") : t("modeToggle.leadGenHint")}
              </span>
            </div>
          )}

          {/* ─── Asset cards row (UGC-style): AVATAR / ZDJĘCIA REFERENCYJNE / INSPIRACJA / [PRODUKT w ecom] ─── */}
          <div className={`grid ${isEcommerceMode ? "grid-cols-4" : "grid-cols-3"} gap-3 mb-3`}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setAvatarModalOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setAvatarModalOpen(true);
                }
              }}
              className={`relative min-w-0 group flex items-center gap-3 px-3 py-3 rounded-xl border transition-all cursor-pointer ${
                activeAvatar
                  ? "bg-gradient-to-br from-white/[0.06] to-white/[0.02] border-white/20 hover:border-white/30"
                  : "bg-white/[0.02] border-white/[0.08] border-dashed hover:border-white/20 hover:bg-white/[0.04]"
              }`}
            >
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 ${
                  activeAvatar?.photoUrl ? "bg-black/40" : "bg-white/[0.04] text-white/50"
                }`}
              >
                {activeAvatar?.photoUrl ? (
                  <img
                    src={activeAvatar.photoUrl}
                    alt={activeAvatar.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Users className="w-5 h-5" />
                )}
              </div>
              <div className="flex flex-col items-start leading-tight min-w-0 flex-1">
                <span className="text-[9px] uppercase tracking-[0.15em] font-semibold text-white/40">
                  Avatar
                </span>
                <span
                  className={`text-sm font-medium truncate ${activeAvatar ? "text-white" : "text-white/50"}`}
                >
                  {activeAvatar?.name ?? "Wybierz avatar..."}
                </span>
              </div>
              {activeAvatar && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveAvatar(null);
                  }}
                  title="Odepnij avatar"
                  className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-5 h-5 rounded-md text-white/40 hover:text-white hover:bg-white/[0.08] border border-transparent hover:border-white/[0.12] transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setReferencesDialogOpen(true)}
              className={`min-w-0 group flex items-center gap-3 px-3 py-3 rounded-xl border transition-all ${
                selectedAssetIds.size > 0
                  ? "bg-gradient-to-br from-white/[0.06] to-white/[0.02] border-white/20 hover:border-white/30"
                  : "bg-white/[0.02] border-white/[0.08] border-dashed hover:border-white/20 hover:bg-white/[0.04]"
              }`}
            >
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 ${
                  selectedAssetIds.size > 0 &&
                  brandAssets.find((a) => selectedAssetIds.has(a.id))?.fileUrl
                    ? "bg-black/40"
                    : "bg-white/[0.04] text-white/50"
                }`}
              >
                {selectedAssetIds.size > 0 &&
                brandAssets.find((a) => selectedAssetIds.has(a.id))?.fileUrl ? (
                  <img
                    src={brandAssets.find((a) => selectedAssetIds.has(a.id))!.fileUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImagePlus className="w-5 h-5" />
                )}
              </div>
              <div className="flex flex-col items-start leading-tight min-w-0 flex-1">
                <span className="text-[9px] uppercase tracking-[0.15em] font-semibold text-white/40">
                  Zdjęcia referencyjne
                </span>
                <span
                  className={`text-sm font-medium truncate ${selectedAssetIds.size > 0 ? "text-white" : "text-white/50"}`}
                >
                  {selectedAssetIds.size > 0
                    ? `${selectedAssetIds.size} wybranych`
                    : "Wybierz / wgraj zdjęcia..."}
                </span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setInspirationDialogOpen(true)}
              className={`min-w-0 group flex items-center gap-3 px-3 py-3 rounded-xl border transition-all ${
                inspirationFiles.length > 0 || pinterestPreviews.some((p) => p.selected)
                  ? "bg-gradient-to-br from-white/[0.06] to-white/[0.02] border-white/20 hover:border-white/30"
                  : "bg-white/[0.02] border-white/[0.08] border-dashed hover:border-white/20 hover:bg-white/[0.04]"
              }`}
            >
              <div
                className={`flex items-center justify-center w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 ${
                  inspirationFiles.length > 0 || pinterestPreviews.some((p) => p.selected)
                    ? "bg-black/40"
                    : "bg-white/[0.04] text-white/50"
                }`}
              >
                {inspirationFiles.length > 0 &&
                (inspirationFiles[0].preview || inspirationFiles[0].url) ? (
                  <img
                    src={inspirationFiles[0].preview || inspirationFiles[0].url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : pinterestPreviews.find((p) => p.selected) ? (
                  <img
                    src={pinterestPreviews.find((p) => p.selected)!.imageUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Sparkles className="w-5 h-5" />
                )}
              </div>
              <div className="flex flex-col items-start leading-tight min-w-0 flex-1">
                <span className="text-[9px] uppercase tracking-[0.15em] font-semibold text-white/40">
                  Inspiracja
                </span>
                <span
                  className={`text-sm font-medium truncate ${
                    inspirationFiles.length > 0 || pinterestPreviews.some((p) => p.selected)
                      ? "text-white"
                      : "text-white/50"
                  }`}
                >
                  {(() => {
                    const totalSel =
                      inspirationFiles.length + pinterestPreviews.filter((p) => p.selected).length;
                    return totalSel > 0
                      ? `${totalSel} referencj${totalSel === 1 ? "a" : "i"}`
                      : "Dodaj inspirację...";
                  })()}
                </span>
              </div>
            </button>
            {isEcommerceMode && (
              <button
                type="button"
                onClick={() => setProductModalOpen(true)}
                className={`min-w-0 group flex items-center gap-3 px-3 py-3 rounded-xl border transition-all ${
                  selectedProduct
                    ? "bg-gradient-to-br from-orange-500/[0.10] to-red-500/[0.04] border-orange-500/30 hover:border-orange-400/45"
                    : "bg-white/[0.02] border-white/[0.08] border-dashed hover:border-orange-500/30 hover:bg-white/[0.04]"
                }`}
              >
                <div
                  className={`flex items-center justify-center w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 ${
                    selectedProduct?.imageUrls?.[0]
                      ? "bg-black/40"
                      : "bg-white/[0.04] text-white/50"
                  }`}
                >
                  {selectedProduct?.imageUrls?.[0] ? (
                    <img
                      src={selectedProduct.imageUrls[0]}
                      alt={selectedProduct.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ShoppingBag className="w-5 h-5" />
                  )}
                </div>
                <div className="flex flex-col items-start leading-tight min-w-0 flex-1">
                  <span className="text-[9px] uppercase tracking-[0.15em] font-semibold text-orange-300/70">
                    Produkt
                  </span>
                  <span
                    className={`text-sm font-medium truncate ${selectedProduct ? "text-white" : "text-white/50"}`}
                  >
                    {selectedProduct?.name ?? "Wybierz / dodaj produkt..."}
                  </span>
                </div>
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-white/[0.05] border border-white/[0.1] text-white/60 hover:text-white hover:border-white/[0.2] transition-colors text-xl leading-none"
                >
                  +
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="top"
                sideOffset={12}
                className="w-52 p-2 bg-[#0c0e14] border border-white/[0.08] rounded-xl shadow-2xl shadow-black/60 text-white"
              >
                <div className="space-y-0.5">
                  {(!isOrganic
                    ? [
                        {
                          label: t("popoverMenu.bestAds"),
                          active: showBestCreatives,
                          toggle: () => setShowBestCreatives(!showBestCreatives),
                        },
                      ]
                    : []
                  ).map(({ label, active, toggle }) => (
                    <button
                      key={label}
                      type="button"
                      onClick={toggle}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-[12px] font-medium transition-colors ${active ? "bg-white/[0.08] text-white" : "text-white/60 hover:bg-white/[0.05] hover:text-white/90"}`}
                    >
                      {label}
                      {active && <Check className="w-3.5 h-3.5 text-[#ef3e29]" />}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <div className="flex-1 flex flex-col bg-white/[0.04] border border-white/[0.08] rounded-2xl min-w-0 overflow-hidden">
              {/* Prompt area — auto-grows up to 240px, then scrolls */}
              <div className="flex items-start gap-3 px-4 pt-3 pb-2">
                <div className="pt-0.5 shrink-0">
                  {/* VP-179: zapisujemy pełną strukturę (wierność), a wstawiamy przez
                      showSavedPrompt, żeby w polu pojawiło się streszczenie. */}
                  <SavedPromptsPicker
                    currentPrompt={resolvePromptForApi(prompt)}
                    onSelect={showSavedPrompt}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // VP-179: do dopracowania podajemy pełną strukturę, nie streszczenie —
                    // inaczej każde kolejne przejście przez AI gubiłoby szczegóły.
                    setCraftIdea(resolvePromptForApi(prompt).trim());
                    setCraftedPrompt("");
                    setCraftedPromptPayload(null);
                    setCraftRefImage(null);
                    setCraftModalOpen(true);
                  }}
                  title="Prompt + AI — opisz pomysl, AI zbuduje pelny prompt"
                  className="shrink-0 mt-0.5 inline-flex items-center gap-1 px-2 h-6 rounded-md bg-gradient-to-br from-violet-600/20 to-fuchsia-600/15 border border-violet-500/30 text-violet-200 hover:text-white hover:border-violet-400/60 transition-all text-[10px] font-medium"
                >
                  <Sparkles className="w-3 h-3" />
                  <span>AI</span>
                </button>
                <textarea
                  ref={promptTextareaRef}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && prompt.trim()) {
                      e.preventDefault();
                      handleGenerate();
                    }
                  }}
                  placeholder={
                    isOrganic
                      ? t("canvas.organicPromptPlaceholder")
                      : t("canvas.adPromptPlaceholder")
                  }
                  rows={1}
                  className="flex-1 min-w-0 bg-transparent text-[13px] leading-[1.55] text-white placeholder:text-white/35 outline-none resize-none overflow-y-auto custom-scrollbar"
                  style={{ maxHeight: 240 }}
                />
                {prompt.trim() ? (
                  <button
                    onClick={() => setPrompt("")}
                    title="Wyczyść prompt"
                    className="shrink-0 mt-0.5 text-white/30 hover:text-white/70 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <button
                    onClick={async () => {
                      try {
                        const text = await navigator.clipboard.readText();
                        if (text.trim()) setPrompt(text.trim().slice(0, 5000));
                      } catch {
                        toast({
                          title: "Schowek niedostępny",
                          description: "Przeglądarka zablokowała dostęp do schowka",
                          variant: "destructive",
                        });
                      }
                    }}
                    title="Wklej ze schowka"
                    className="shrink-0 mt-0.5 text-white/30 hover:text-white/70 transition-colors text-[10px] font-medium"
                  >
                    Wklej
                  </button>
                )}
              </div>
              {/* Toolbar — pill controls along the bottom of the prompt panel */}
              <div className="flex items-center gap-2 flex-wrap px-3 pb-2.5 pt-1 border-t border-white/[0.05]">
                <ImageModelPicker
                  models={IMAGE_MODELS}
                  value={imageModel}
                  onChange={setImageModel}
                />
                {/* Quality dropdown — single button, opens popover with options. */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      title="Jakość obrazu"
                      className="shrink-0 flex items-center gap-1.5 px-2.5 h-7 rounded-lg border bg-gradient-to-br from-blue-600/15 via-violet-600/10 to-fuchsia-600/15 border-blue-500/30 text-white/85 hover:text-white hover:border-blue-400/50 transition-all text-xs font-medium"
                    >
                      <Sparkles className="w-3 h-3 text-blue-300" />
                      <span>Jakość</span>
                      <span className="text-blue-300 font-mono">{resolution}</span>
                      <ChevronDown className="w-3 h-3 opacity-60" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="center"
                    side="top"
                    sideOffset={10}
                    className="w-44 p-1.5 bg-[#0c0e14] border border-white/[0.1] rounded-xl shadow-2xl shadow-black/60"
                  >
                    <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-white/40">
                      Jakość obrazu
                    </div>
                    {IMAGE_RESOLUTIONS.filter((r) => r.models.includes(imageModel)).map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setResolution(r.id)}
                        className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-[12px] transition-colors ${
                          resolution === r.id
                            ? "bg-blue-600/15 text-blue-200"
                            : "text-white/70 hover:bg-white/[0.05] hover:text-white"
                        }`}
                      >
                        <span className="font-medium">{r.label}</span>
                        <span
                          className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                            r.id === "0.5K"
                              ? "bg-emerald-500/20 text-emerald-400"
                              : r.id === "1K"
                                ? "bg-white/5 text-white/50"
                                : r.id === "2K"
                                  ? "bg-amber-500/20 text-amber-400"
                                  : "bg-orange-500/20 text-orange-400"
                          }`}
                        >
                          {r.costLabel}
                        </span>
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>
                <FormatPicker selectedFormats={selectedFormats} toggleFormat={toggleFormat} />
                <div className="flex items-center gap-1 shrink-0 px-2 h-7 rounded-lg border bg-white/[0.04] border-white/[0.1]">
                  {inspirationDriven ? (
                    <span
                      className="text-[12px] text-white/85 tabular-nums px-1 text-center font-mono"
                      title={t("inspiration.onePerGraphicHint")}
                    >
                      💡 {effectiveQuantity}
                    </span>
                  ) : (
                    <>
                      <button
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                        className="w-5 h-5 flex items-center justify-center text-white/50 hover:text-white transition-colors text-base leading-none select-none"
                      >
                        —
                      </button>
                      <span className="text-[12px] text-white/85 tabular-nums w-9 text-center font-mono">
                        {quantity}/20
                      </span>
                      <button
                        onClick={() => setQuantity((q) => Math.min(20, q + 1))}
                        className="w-5 h-5 flex items-center justify-center text-white/50 hover:text-white transition-colors text-base leading-none select-none"
                      >
                        +
                      </button>
                    </>
                  )}
                </div>
                {!isOrganic && (
                  <button
                    type="button"
                    onClick={() => {
                      setPreviewCopyMode(!previewCopyMode);
                      setCopyPreview([]);
                    }}
                    title={t("copyPreview.toggleLabel")}
                    className={`flex items-center gap-1.5 shrink-0 px-2.5 h-7 rounded-lg border transition-all ${
                      previewCopyMode
                        ? "bg-amber-500/15 border-amber-400/40 text-amber-200"
                        : "bg-white/[0.04] border-white/[0.1] text-white/50 hover:text-white hover:border-white/20"
                    }`}
                  >
                    <Eye className="w-3.5 h-3.5" />
                    <span className="text-[11px] font-medium">{t("copyPreview.toggleLabel")}</span>
                  </button>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={
                !prompt.trim() ||
                selectedFormats.length === 0 ||
                generating ||
                optimizePromptMutation.isPending ||
                copyPreviewMutation.isPending
              }
              className="shrink-0 flex items-center gap-1.5 px-5 h-11 rounded-xl bg-[#ef3e29] hover:bg-[#ef3e29]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-white font-bold text-[13px]"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t(generateLoadingMessage)}
                </>
              ) : copyPreview.length > 0 ? (
                <>
                  <Sparkles className="w-4 h-4" />
                  {t("canvas.generateWithCopy")}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  {t("canvas.generateCount", { count: effectiveQuantity })}
                </>
              )}
            </button>
          </div>
          {/* Avatar auto-suggest: jeśli prompt wspomina nazwę nieaktywnego avatara */}
          {(() => {
            const lowered = prompt.toLowerCase();
            if (lowered.length < 4) return null;
            const match = allAvatars.find((a) => {
              if (a.id === activeAvatar?.id) return false;
              if (a.id === dismissedSuggestionFor) return false;
              const nameLower = a.name.toLowerCase();
              // Match only on word boundary to avoid false positives ("Karol" in "karuzela")
              const re = new RegExp(
                `(^|\\W)${nameLower.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(\\W|$)`,
                "i"
              );
              return re.test(prompt);
            });
            if (!match) return null;
            return (
              <div className="mt-1.5 flex items-center gap-2 px-2 py-1.5 rounded-lg border border-amber-500/30 bg-amber-500/[0.06]">
                <span className="text-[10px] text-amber-200">
                  💡 Wykryto wzmiankę o avatarze <strong>{match.name}</strong>. Aktywować go?
                </span>
                <button
                  onClick={() => setActiveAvatar(match.id)}
                  className="ml-auto px-2 py-0.5 rounded text-[10px] font-medium bg-amber-500/20 border border-amber-500/40 text-amber-100 hover:bg-amber-500/30 transition-colors"
                >
                  Aktywuj
                </button>
                <button
                  onClick={() => setDismissedSuggestionFor(match.id)}
                  className="text-[10px] text-amber-200/50 hover:text-amber-100 transition-colors"
                  title="Pomiń tę sugestię"
                >
                  ✕
                </button>
              </div>
            );
          })()}
          {/* Active context summary — pokazuje co AI dostanie z generacji */}
          {(activeAvatar ||
            selectedAssetIds.size > 0 ||
            inspirationFiles.length > 0 ||
            pinterestPreviews.some((p) => p.selected) ||
            selectedLayout) && (
            <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
              <span className="text-[9px] text-white/35">Aktywny kontekst:</span>
              {activeAvatar && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-emerald-600/15 border border-emerald-500/40 text-emerald-200">
                  ✨ {activeAvatar.name}
                </span>
              )}
              {selectedAssetIds.size > 0 && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-red-600/15 border border-red-500/40 text-red-200">
                  🖼️ {selectedAssetIds.size} brand assets
                </span>
              )}
              {inspirationFiles.length > 0 && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-600/15 border border-blue-500/40 text-blue-200">
                  💡 {inspirationFiles.length} inspiracji
                </span>
              )}
              {pinterestPreviews.filter((p) => p.selected).length > 0 && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-pink-600/15 border border-pink-500/40 text-pink-200">
                  📌 {pinterestPreviews.filter((p) => p.selected).length} pinów
                </span>
              )}
              {selectedLayout && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-violet-600/15 border border-violet-500/40 text-violet-200">
                  📐{" "}
                  {LAYOUT_TEMPLATES.find((l) => l.id === selectedLayout)?.label || selectedLayout}
                </span>
              )}
              <button
                onClick={() => {
                  setActiveAvatar(null);
                  setSelectedAssetIds(new Set());
                  setInspirationFiles([]);
                  setPinterestPreviews([]);
                  setSelectedLayout(null);
                  setSelectedExampleId(null);
                }}
                title="Odepnij avatar, brand assets, inspiracje i layout"
                className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] text-white/40 hover:text-white hover:bg-white/[0.06] border border-transparent hover:border-white/[0.1] transition-colors"
              >
                ✕ Wyczyść kontekst
              </button>
            </div>
          )}
          <div className="flex items-center justify-center gap-3 mt-1.5 text-[9px] text-white/30">
            <span>
              <kbd className="px-1 py-0.5 rounded bg-white/[0.04] border border-white/[0.08]">
                ↵
              </kbd>{" "}
              generuj
            </span>
            <span>
              <kbd className="px-1 py-0.5 rounded bg-white/[0.04] border border-white/[0.08]">
                ⌘K
              </kbd>{" "}
              avatar
            </span>
            <span>
              <kbd className="px-1 py-0.5 rounded bg-white/[0.04] border border-white/[0.08]">
                ⌘S
              </kbd>{" "}
              zapisz prompt
            </span>
            <span>
              <kbd className="px-1 py-0.5 rounded bg-white/[0.04] border border-white/[0.08]">
                ?
              </kbd>{" "}
              wszystkie skróty
            </span>
          </div>
        </div>

        {/* ─── CANVAS (gallery / hero) ─────────────────────── */}
        <div className="[order:1] flex-1 min-h-0 overflow-y-auto">
          {!hasGraphics ? (
            <div className="flex flex-col items-center h-full px-6 select-none py-10">
              <p className="text-[15px] text-white/55 text-center max-w-md mb-1">
                {isOrganic ? t("canvas.organicPlaceholder") : t("canvas.adPlaceholder")}
              </p>
              <p className="text-[11px] text-white/35 text-center mb-5">
                Wybierz przykład — załaduje prompt i ustawienia, możesz wygenerować od razu lub
                edytować pod swój produkt.
              </p>
              {spinning ? (
                <div ref={reelViewportRef} className="relative w-full max-w-5xl overflow-hidden">
                  <div
                    className="flex will-change-transform"
                    style={{
                      transform: `translateX(${reelX}px)`,
                      transition:
                        reelX !== 0
                          ? `transform ${REEL_SPIN_DURATION}s cubic-bezier(0.06, 0.85, 0.18, 1)`
                          : "none",
                    }}
                  >
                    {reelStrip.map((c, k) => (
                      <div key={k} className="w-1/4 shrink-0 px-1.5">
                        <div className="rounded-xl border border-white/[0.1] overflow-hidden aspect-[3/4] bg-white/[0.02]">
                          <img src={c.thumbnail} alt="" className="w-full h-full object-cover" />
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* edge fades + center landing marker (roulette feel) */}
                  <div className="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-[#07090e] via-[#07090e]/80 to-transparent" />
                  <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-[#07090e] via-[#07090e]/80 to-transparent" />
                  <div className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-amber-400/40" />
                </div>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-5xl w-full">
                  {visibleInspo.map((card, i) => {
                    if (card.kind === "template") {
                      const ex = card.ex;
                      const styleMeta = ex.style ? STYLES.find((s) => s.id === ex.style) : null;
                      const layoutMeta = ex.layout
                        ? LAYOUT_TEMPLATES.find((l) => l.id === ex.layout)
                        : null;
                      const isSelected = selectedExampleId === ex.id;
                      return (
                        <div
                          key={card.key}
                          className={`group flex flex-col rounded-xl border overflow-hidden transition-all ${isSelected ? "border-red-500/60 ring-1 ring-red-500/30 bg-red-600/[0.06]" : "border-white/[0.08] bg-white/[0.02] hover:border-white/[0.18] hover:bg-white/[0.04]"}`}
                        >
                          <button
                            type="button"
                            onClick={() => setPreviewExample(ex)}
                            className="relative w-full aspect-[3/4] overflow-hidden"
                            title={t("popoverMenu.preview")}
                          >
                            <img
                              src={ex.thumbnail}
                              alt={ex.label}
                              className="w-full h-full object-cover transition-transform group-hover:scale-[1.03]"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                            <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                              <span className="text-[11px] font-medium text-white/95 truncate">
                                {ex.label}
                              </span>
                              <ZoomIn className="w-3 h-3 text-white/60 shrink-0 ml-1" />
                            </div>
                          </button>
                          <div className="p-2.5 space-y-2">
                            <div className="flex flex-wrap gap-1">
                              {styleMeta && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-white/[0.06] border border-white/[0.1] text-white/70">
                                  <span>{styleMeta.emoji}</span>
                                  {t(styleMeta.label)}
                                </span>
                              )}
                              {layoutMeta && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-violet-600/15 border border-violet-500/30 text-violet-200">
                                  📐 {t(layoutMeta.label)}
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => handleUseExample(ex)}
                              className={`w-full flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${isSelected ? "bg-red-600/40 text-red-100" : "bg-red-600/20 hover:bg-red-600/35 text-red-200"}`}
                            >
                              <Sparkles className="w-3 h-3" />{" "}
                              {isSelected ? t("popoverMenu.selected") : t("popoverMenu.use")}
                            </button>
                          </div>
                        </div>
                      );
                    }
                    // Gallery inspiration card (Viral Partners gallery).
                    return (
                      <div
                        key={card.key}
                        className="group flex flex-col rounded-xl border overflow-hidden transition-all border-white/[0.08] bg-white/[0.02] hover:border-white/[0.18] hover:bg-white/[0.04]"
                      >
                        <button
                          type="button"
                          onClick={() => setExpandedImage(card.thumbnail)}
                          className="relative w-full aspect-[3/4] overflow-hidden"
                          title={t("popoverMenu.preview")}
                        >
                          <img
                            src={card.thumbnail}
                            alt={card.label}
                            className="w-full h-full object-cover transition-transform group-hover:scale-[1.03]"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                            <span className="text-[11px] font-medium text-white/95 truncate">
                              {card.label}
                            </span>
                            <ZoomIn className="w-3 h-3 text-white/60 shrink-0 ml-1" />
                          </div>
                        </button>
                        <div className="p-2.5 space-y-2">
                          <div className="flex flex-wrap gap-1">
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-600/15 border border-amber-500/30 text-amber-200">
                              ✨ Galeria VP
                            </span>
                          </div>
                          <button
                            onClick={() => handleUseGalleryItem(card)}
                            className="w-full flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors bg-red-600/20 hover:bg-red-600/35 text-red-200"
                          >
                            <Sparkles className="w-3 h-3" /> {t("popoverMenu.use")}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {inspoPool.length > 4 && (
                <button
                  type="button"
                  onClick={shuffleInspo}
                  disabled={spinning}
                  className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] text-[11px] font-medium text-amber-200/90 hover:text-amber-100 hover:border-amber-400/50 hover:bg-amber-500/[0.12] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Shuffle className={`w-3.5 h-3.5 ${spinning ? "animate-spin" : ""}`} />{" "}
                  {spinning ? "Losuję…" : "Wylosuj kolejne"}
                </button>
              )}
              <p className="text-[10px] text-white/30 mt-5">
                Kliknij <span className="text-white/55">Użyj</span>, by załadować prompt i
                ustawienia — albo wpisz własny prompt poniżej.
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {/* Progress indicator */}

              {/* VP-206: kolejka równoległych generowań — wiersz per job ze
                  stabilnym numerem zadania i własnym paskiem postępu. */}
              {anyGenerating && (
                <div className="p-4 bg-white/[0.03] border border-white/[0.08] rounded-xl space-y-3">
                  {activeGenJobIds.map((jobId) => (
                    <QueueRow
                      key={jobId}
                      label={
                        genJobPrompts[jobId]?.trim() ||
                        t("queue.jobLabel", { index: genJobTaskNo[jobId] ?? "…" })
                      }
                      realProgress={genJobProgress[jobId]}
                    />
                  ))}
                  {generateMutation.isPending && (
                    <QueueRow
                      label={
                        prompt.trim() || t("queue.jobLabel", { index: taskCounterRef.current + 1 })
                      }
                    />
                  )}
                  <p className="text-xs text-white/50">
                    {genSlotsFull
                      ? t("queue.slotsFull", { limit: MAX_CONCURRENT_AD_GRAPHIC_JOBS })
                      : t("queue.subtitle", {
                          count: activeGenJobIds.length,
                          limit: MAX_CONCURRENT_AD_GRAPHIC_JOBS,
                        })}
                  </p>
                </div>
              )}

              {/* Results Gallery — shown progressively as images arrive */}
              {graphics.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm font-semibold text-white">
                        {t("canvas.generatedTitle", { count: graphics.length })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isOrganic && selectedGraphicIds.size > 0 && (
                        <Button
                          size="sm"
                          onClick={handleSendToCampaignCreator}
                          className="text-xs bg-red-600/20 text-red-300 hover:bg-red-600/30 border border-red-500/30"
                        >
                          <Rocket className="w-3.5 h-3.5 mr-1" />
                          {t("canvas.toCampaign", { count: selectedGraphicIds.size })}
                        </Button>
                      )}
                      {!isOrganic && (
                        <Button
                          size="sm"
                          onClick={handleSendAllToCampaignCreator}
                          className="text-xs bg-red-600/20 text-red-300 hover:bg-red-600/30 border border-red-500/30"
                        >
                          <Rocket className="w-3.5 h-3.5 mr-1" />
                          {t("canvas.allToCampaign")}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={handleDownloadAll}
                        className="text-xs bg-red-600/20 text-red-300 hover:bg-red-600/30 border border-red-500/30"
                      >
                        <Download className="w-3.5 h-3.5 mr-1" />
                        {t("canvas.download")}
                      </Button>
                      <Button
                        size="sm"
                        onClick={clearAllResults}
                        disabled={anyGenerating}
                        className="text-xs bg-white/[0.06] text-white/50 hover:text-white hover:bg-white/10 border border-white/[0.08]"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        {t("queue.clear")}
                      </Button>
                    </div>
                  </div>
                  {(() => {
                    const feedbackValues = Object.values(graphicFeedback);
                    const ups = feedbackValues.filter((v) => v === "up").length;
                    const downs = feedbackValues.filter((v) => v === "down").length;
                    if (ups + downs === 0) return null;
                    return (
                      <div className="flex items-center gap-3 px-2 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                        <span className="text-[10px] text-white/40">Feedback sesji:</span>
                        {ups > 0 && (
                          <span className="text-[10px] text-emerald-400 flex items-center gap-0.5">
                            <ThumbsUp className="w-3 h-3" /> {ups}
                          </span>
                        )}
                        {downs > 0 && (
                          <span className="text-[10px] text-red-400 flex items-center gap-0.5">
                            <ThumbsDown className="w-3 h-3" /> {downs}
                          </span>
                        )}
                        <span className="text-[10px] text-white/30">
                          ({feedbackValues.length}/{graphics.length} ocenionych)
                        </span>
                      </div>
                    );
                  })()}
                  {/* VP-206: sekcja per zadanie generowania; przy jednej grupie
                      bez nagłówka — galeria wygląda jak dotychczas. */}
                  {graphicsGroups.map((group, groupIndex) => (
                    <div key={group.key} className="space-y-4">
                      {/* Nagłówek tylko dla grup z jobem (ungrouped renderuje się
                          bez nagłówka); numer stabilny z genJobTaskNo — ten sam,
                          który user widział w panelu kolejki. */}
                      {graphicsGroups.length > 1 && group.jobId && (
                        <div className="flex items-center gap-2 pt-1 border-t border-white/[0.06] first:border-t-0 first:pt-0">
                          <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-red-300/80 bg-red-600/10 border border-red-500/20 rounded px-1.5 py-0.5">
                            {t("queue.jobLabel", {
                              index: genJobTaskNo[group.jobId] ?? groupIndex + 1,
                            })}
                          </span>
                          {genJobPrompts[group.jobId]?.trim() && (
                            <span className="text-xs text-white/40 truncate">
                              {genJobPrompts[group.jobId]}
                            </span>
                          )}
                          <span className="ml-auto shrink-0 text-[10px] text-white/30">
                            {group.items.length}
                          </span>
                        </div>
                      )}
                      {FORMATS.filter((f) => group.items.some((g) => g.format === f.id)).map(
                        (format) => {
                          const formatGraphics = group.items.filter((g) => g.format === format.id);
                          return (
                            <div key={format.id}>
                              <h4 className="text-xs font-medium text-white/50 mb-2 flex items-center gap-1.5">
                                <span>{format.icon}</span> {format.label}
                                <span className="text-[10px] font-mono text-[#ef3e29]">
                                  {format.ratio}
                                </span>
                                <span className="text-white/30">({format.size})</span>
                              </h4>
                              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                                {formatGraphics.map((graphic) => {
                                  const isSelected = selectedGraphicIds.has(graphic.id);
                                  return (
                                    <div
                                      key={graphic.id}
                                      className={`group relative bg-white/[0.03] border rounded-xl overflow-hidden hover:border-white/[0.15] transition-all ${
                                        isSelected
                                          ? "border-red-500/60 ring-1 ring-red-500/30"
                                          : "border-white/[0.08]"
                                      }`}
                                    >
                                      <button
                                        onClick={() => toggleGraphicSelection(graphic.id)}
                                        className={`absolute top-2 left-2 z-10 w-5 h-5 rounded-md border flex items-center justify-center transition-all ${
                                          isSelected
                                            ? "bg-red-600 border-red-500 text-white"
                                            : "bg-black/40 border-white/30 text-transparent hover:border-white/60"
                                        }`}
                                      >
                                        <Check className="w-3 h-3" />
                                      </button>
                                      <div
                                        className="relative cursor-pointer"
                                        onClick={() =>
                                          setExpandedImage(
                                            expandedImage === graphic.id ? null : graphic.id
                                          )
                                        }
                                      >
                                        <span className="absolute top-2 right-2 z-10 px-1.5 py-0.5 rounded-md bg-black/60 backdrop-blur-sm text-[10px] font-mono text-white/90 border border-white/10">
                                          {format.ratio}
                                        </span>
                                        <img
                                          src={graphic.imageUrl}
                                          alt={`${format.label} v${graphic.variantIndex}`}
                                          className="w-full h-auto"
                                          loading="lazy"
                                        />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                                          <ZoomIn className="w-6 h-6 text-white" />
                                        </div>
                                      </div>
                                      <div className="p-2 flex items-center justify-between">
                                        <span className="text-[10px] text-white/40">
                                          v{graphic.variantIndex}
                                        </span>
                                        <div className="flex gap-1">
                                          <button
                                            onClick={() => handleDownload(graphic)}
                                            className="p-1.5 rounded-md bg-white/[0.06] hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                                            title="Pobierz"
                                          >
                                            <Download className="w-3.5 h-3.5" />
                                          </button>
                                          <button
                                            onClick={() => {
                                              setPrompt(
                                                `Wariacja tej grafiki: ${prompt || (graphic as { prompt?: string }).prompt || "reklama"}\n\nZachowaj styl wizualny, paletę kolorów i mood, ale zmień kompozycję / wariant układu / detale.`
                                              );
                                              setQuantity(3);
                                              setSelectedFormats([graphic.format as AdFormat]);
                                              toast({
                                                title: "Tryb wariacji",
                                                description:
                                                  "3x z innym układem, ten sam styl. Kliknij Generuj.",
                                              });
                                              document
                                                .querySelector("input[placeholder]")
                                                ?.scrollIntoView({
                                                  behavior: "smooth",
                                                  block: "center",
                                                });
                                            }}
                                            className="p-1.5 rounded-md bg-white/[0.06] hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                                            title="Stwórz 3 wariacje tej grafiki (ten sam styl, inna kompozycja)"
                                          >
                                            <RefreshCw className="w-3.5 h-3.5" />
                                          </button>
                                          <button
                                            onClick={() => {
                                              setEditingGraphicId(
                                                editingGraphicId === graphic.id ? null : graphic.id
                                              );
                                              setEditInstructions("");
                                            }}
                                            className={`p-1.5 rounded-md transition-colors ${editingGraphicId === graphic.id ? "bg-red-600/20 text-red-300 hover:bg-red-600/30" : "bg-white/[0.06] hover:bg-white/10 text-white/60 hover:text-white"}`}
                                            title={t("canvas.editGraphic")}
                                          >
                                            <Wand2 className="w-3.5 h-3.5" />
                                          </button>
                                          <span className="w-px h-4 bg-white/[0.08]" />
                                          <button
                                            onClick={() => handleGraphicFeedback(graphic.id, "up")}
                                            className={`p-1.5 rounded-md transition-colors ${graphicFeedback[graphic.id] === "up" ? "bg-emerald-600/20 text-emerald-300" : "bg-white/[0.06] hover:bg-white/10 text-white/60 hover:text-emerald-300"}`}
                                            title="Dobra grafika"
                                          >
                                            <ThumbsUp className="w-3.5 h-3.5" />
                                          </button>
                                          <button
                                            onClick={() =>
                                              handleGraphicFeedback(graphic.id, "down")
                                            }
                                            className={`p-1.5 rounded-md transition-colors ${graphicFeedback[graphic.id] === "down" ? "bg-red-600/20 text-red-300" : "bg-white/[0.06] hover:bg-white/10 text-white/60 hover:text-red-300"}`}
                                            title={t("canvas.badGraphic")}
                                          >
                                            <ThumbsDown className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      </div>
                                      {feedbackReasonId === graphic.id &&
                                        graphicFeedback[graphic.id] && (
                                          <div className="px-3 py-2 border-t border-white/[0.06] bg-white/[0.02]">
                                            <div className="flex gap-1.5 items-end">
                                              <input
                                                type="text"
                                                value={feedbackReasonText}
                                                onChange={(e) =>
                                                  setFeedbackReasonText(e.target.value)
                                                }
                                                onKeyDown={(e) => {
                                                  if (e.key === "Enter")
                                                    handleSubmitFeedbackReason(graphic.id);
                                                }}
                                                placeholder={
                                                  graphicFeedback[graphic.id] === "up"
                                                    ? t("canvas.feedbackLiked")
                                                    : t("canvas.feedbackDisliked")
                                                }
                                                className="flex-1 h-7 px-2 text-[11px] rounded-md bg-[#030407] border border-white/[0.1] text-white placeholder:text-white/30 focus:outline-none focus:border-white/20"
                                                autoFocus
                                              />
                                              <button
                                                onClick={() =>
                                                  handleSubmitFeedbackReason(graphic.id)
                                                }
                                                className="h-7 px-2.5 text-[10px] rounded-md bg-white/[0.08] hover:bg-white/[0.12] text-white/70 hover:text-white transition-colors"
                                              >
                                                {feedbackReasonText.trim()
                                                  ? t("copyPreview.saveButton")
                                                  : t("copyPreview.skipButton")}
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                      {editingGraphicId === graphic.id && (
                                        <div className="p-3 pt-2 space-y-2 border-t border-white/[0.06] bg-white/[0.02]">
                                          <Textarea
                                            value={editInstructions}
                                            onChange={(e) => setEditInstructions(e.target.value)}
                                            placeholder={t("canvas.editHint")}
                                            className="min-h-[56px] text-xs bg-[#030407] border-white/[0.1] text-white placeholder:text-white/30 resize-none"
                                          />
                                          {(() => {
                                            const editModel = graphic.imageModel || imageModel;
                                            const isEditCapable = [
                                              "nano-banana-pro",
                                              "nano-banana-2",
                                              "seedream-4.5",
                                              "seedream-5.0-lite",
                                              "seedream-4.0",
                                              "gpt-image-1",
                                              "gpt-image-2",
                                            ].includes(editModel);
                                            if (isEditCapable)
                                              return (
                                                <p className="text-[10px] text-emerald-400/70 flex items-center gap-1">
                                                  <Wand2 className="w-3 h-3" />{" "}
                                                  {t("canvas.editModel", { model: editModel })}
                                                </p>
                                              );
                                            return (
                                              <p className="text-[10px] text-white/30">
                                                Nowa grafika z instrukcjami (model nie wspiera
                                                edycji)
                                              </p>
                                            );
                                          })()}
                                          <Button
                                            size="sm"
                                            onClick={() => handleRegenerate(graphic)}
                                            disabled={regenerating || !editInstructions.trim()}
                                            className="w-full h-8 text-xs bg-red-600/80 hover:bg-red-600 text-white"
                                          >
                                            {regenerating ? (
                                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                            ) : (
                                              <Wand2 className="w-3 h-3 mr-1" />
                                            )}
                                            {t("canvas.improveButton")}
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  ))}
                  {lastGenMeta && (
                    <div className="flex items-center gap-2 text-xs text-white/40 px-1">
                      <span>
                        {t("canvas.tokensUsed", { deducted: lastGenMeta.tokensDeducted })}
                      </span>
                      <span>•</span>
                      <span>
                        {t("canvas.tokensLeft", { remaining: lastGenMeta.tokensRemaining })}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ─── Expanded image modal ─────────────────────── */}
        {expandedImage && (
          <div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setExpandedImage(null)}
          >
            <button
              onClick={() => setExpandedImage(null)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              // VP-121: `expandedImage` carries either a generated graphic's id
              // (canvas zoom) or a gallery inspiration URL (Galeria VP zoom). Resolve
              // the id to its imageUrl; otherwise the value is already a direct URL.
              src={graphics.find((g) => g.id === expandedImage)?.imageUrl ?? expandedImage}
              alt={t("canvas.zoomAlt")}
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* ─── Example preview modal ────────────────────── */}
        {previewExample && (
          <div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setPreviewExample(null)}
          >
            <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setPreviewExample(null)}
                className="absolute -top-3 -right-3 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white z-10"
              >
                <X className="w-5 h-5" />
              </button>
              <img
                src={previewExample.thumbnail}
                alt={previewExample.label}
                className="w-full h-auto object-contain rounded-xl"
              />
              <div className="mt-3 flex items-center justify-between">
                <p className="text-sm text-white font-medium">{previewExample.label}</p>
                <button
                  onClick={() => {
                    handleUseExample(previewExample);
                    setPreviewExample(null);
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
                >
                  <Sparkles className="w-4 h-4" /> {t("popoverMenu.useExample")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Product picker (UGC studio backend) ─── */}
        <ProductPickerModal
          open={productModalOpen}
          onClose={() => setProductModalOpen(false)}
          type="product"
          selectedId={selectedProduct?.id ?? null}
          onSelect={(p) => {
            setSelectedProduct(p);
            setProductModalOpen(false);
          }}
        />

        {/* ─── Avatar picker (UGC studio backend) — auto-imports as persona so prompt builder still works ─── */}
        <AvatarPickerModal
          open={avatarModalOpen}
          onClose={() => setAvatarModalOpen(false)}
          selectedId={null}
          onSelect={async (ugc: UgcAvatar | null) => {
            if (!ugc) {
              setActiveAvatar(null);
              setAvatarModalOpen(false);
              return;
            }
            try {
              const existing = allAvatars.find(
                (p) => p.name.trim().toLowerCase() === ugc.name.trim().toLowerCase()
              );
              if (existing) {
                if (existing.photoUrl !== ugc.signedUrl) {
                  await updateAvatar(existing.id, {
                    photoUrl: ugc.signedUrl,
                    usePhotoByDefault: true,
                  });
                }
                setActiveAvatar(existing.id);
              } else {
                const created = await createAvatar({
                  name: ugc.name,
                  contextText: "",
                  photoUrl: ugc.signedUrl,
                  usePhotoByDefault: true,
                });
                setActiveAvatar(created.id);
              }
            } catch (err) {
              toast({
                title: "Nie udało się aktywować avatara",
                description: (err as Error).message,
                variant: "destructive",
              });
            } finally {
              setAvatarModalOpen(false);
            }
          }}
        />

        {/* ─── References (brand-assets) quick dialog ─── */}
        <Dialog open={referencesDialogOpen} onOpenChange={setReferencesDialogOpen}>
          <DialogContent className="max-w-2xl bg-[#0a0a0a] border-white/[0.08]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white">
                <ImagePlus className="w-5 h-5 text-emerald-400" />
                Zdjęcia referencyjne
                {selectedAssetIds.size > 0 && (
                  <span className="text-xs text-emerald-300/80 font-normal">
                    — wybrano {selectedAssetIds.size}
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <input
                ref={refsDialogInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  if (files.length > 0) void handleRefsBulkUpload(files);
                  if (refsDialogInputRef.current) refsDialogInputRef.current.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => refsDialogInputRef.current?.click()}
                disabled={refsBulkUploading}
                className="w-full flex items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed border-white/[0.12] text-sm text-white/60 hover:text-white hover:border-emerald-500/40 hover:bg-emerald-500/[0.03] transition-all disabled:opacity-50"
              >
                {refsBulkUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Wgrywanie…
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" /> Wgraj zdjęcia (możesz wybrać wiele)
                  </>
                )}
              </button>
              {brandAssets.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] uppercase tracking-[0.15em] text-white/40">
                      Twoje zdjęcia ({brandAssets.length})
                    </span>
                    {selectedAssetIds.size > 0 && (
                      <button
                        onClick={() => setSelectedAssetIds(new Set())}
                        className="text-[10px] text-white/40 hover:text-white"
                      >
                        Wyczyść wybór
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-2 max-h-[50vh] overflow-y-auto">
                    {brandAssets.map((asset) => {
                      const isSelected = selectedAssetIds.has(asset.id);
                      return (
                        <div
                          key={asset.id}
                          onClick={() => toggleAssetSelection(asset.id)}
                          className={`relative cursor-pointer rounded-lg overflow-hidden border transition-all group ${
                            isSelected
                              ? "border-emerald-500/70 ring-2 ring-emerald-500/40"
                              : "border-white/[0.08] hover:border-white/[0.2]"
                          }`}
                        >
                          <div className="w-full aspect-square bg-black/40 flex items-center justify-center">
                            <img
                              src={asset.fileUrl}
                              alt={asset.name}
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                          {isSelected && (
                            <div className="absolute top-1 left-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAssetDelete(asset.id);
                            }}
                            className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white/40 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                          <div className="px-1.5 py-1">
                            <p className="text-[10px] text-white/70 truncate">{asset.name}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {brandAssets.length === 0 && !refsBulkUploading && (
                <p className="text-[11px] text-white/40 text-center py-2">
                  Brak zdjęć — wgraj pierwsze powyżej
                </p>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReferencesDialogOpen(false)}
                className="text-white/60"
              >
                Zamknij
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ─── Inspiration quick-add dialog ─── */}
        <Dialog open={inspirationDialogOpen} onOpenChange={setInspirationDialogOpen}>
          <DialogContent className="max-w-md bg-[#0a0a0a] border-white/[0.08]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white">
                <ImagePlus className="w-5 h-5 text-blue-400" />
                Dodaj inspirację
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-[11px] text-white/60 mb-1 block">Wgraj zdjęcie</Label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  id="inspiration-dialog-file-input"
                  onChange={handleInspirationUpload}
                />
                <button
                  type="button"
                  onClick={() => document.getElementById("inspiration-dialog-file-input")?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setInspirationDragOver(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setInspirationDragOver(false);
                  }}
                  onDrop={handleInspirationDrop}
                  className={`w-full flex flex-col items-center justify-center gap-1.5 px-3 py-4 rounded-lg border border-dashed text-[11px] transition-all ${inspirationDragOver ? "border-blue-400/60 bg-blue-400/[0.06] text-blue-200" : "border-white/[0.15] text-white/60 hover:text-white hover:border-white/[0.3]"}`}
                >
                  <Upload className="w-4 h-4" />
                  <span>
                    {inspirationDragOver
                      ? "Upuść zdjęcia tutaj"
                      : "Przeciągnij, wklej (Ctrl+V) lub wybierz plik z dysku"}
                  </span>
                </button>
              </div>
              {!isOrganic && (
                <div>
                  <Label className="text-[11px] text-white/60 mb-1 block flex items-center gap-1.5">
                    <Search className="w-3 h-3 text-red-400/80" /> {t("landingPage.label")}
                  </Label>
                  <Input
                    value={landingPageUrl}
                    onChange={(e) => setLandingPageUrl(e.target.value)}
                    placeholder={t("landingPage.placeholder")}
                    className="h-8 bg-[#030407] border-white/[0.1] text-white text-xs placeholder:text-white/30"
                  />
                  <p className="text-[10px] text-white/30 mt-1">{t("landingPage.hint")}</p>
                </div>
              )}
              {inspirationFiles.length > 0 && (
                <div className="text-[10px] text-emerald-300/80 pt-1">
                  ✓ Aktywne: {inspirationFiles.length} referencj
                  {inspirationFiles.length === 1 ? "a" : "i"}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setInspirationDialogOpen(false)}
                className="text-white/60"
              >
                Zamknij
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ─── YouTube Thumbnail from transcript modal ─────── */}
        <Dialog open={ytThumbModalOpen} onOpenChange={setYtThumbModalOpen}>
          <DialogContent className="max-w-2xl bg-[#0a0a0a] border-white/[0.08]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white">
                <Youtube className="w-5 h-5 text-red-500" />
                Miniaturka YouTube z transkryptu
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-white/50 leading-relaxed">
                Wklej transkrypt filmu (lub sam temat / tezę). AI wyciągnie najmocniejszy hook,
                dobierze emocję i przygotuje gotowy prompt do miniaturki 16:9.
                {brandAssets.filter((a) => a.fileType === "person" || a.fileType === "photo")
                  .length === 0 && (
                  <span className="block mt-1.5 text-amber-400/80">
                    💡 Tip: dodaj w sekcji „Brand" zdjęcie swojej twarzy (typ{" "}
                    <span className="font-mono">person</span> lub{" "}
                    <span className="font-mono">photo</span>) i zaznacz je przed Generuj — model
                    użyje Twojej twarzy na miniaturce.
                  </span>
                )}
              </p>
              <Textarea
                value={ytTranscript}
                onChange={(e) => setYtTranscript(e.target.value)}
                placeholder="Wklej tu transkrypt filmu, jego temat lub kluczowe punkty..."
                className="min-h-[200px] bg-white/[0.03] border-white/[0.08] text-white text-sm resize-none"
                disabled={ytThumbnailFromTranscriptMutation.isPending}
              />
              <div className="text-[10px] text-white/30 text-right">
                {ytTranscript.length} / 50000 znaków
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setYtThumbModalOpen(false)}
                disabled={ytThumbnailFromTranscriptMutation.isPending}
                className="bg-transparent border-white/[0.12] text-white/70 hover:bg-white/[0.06]"
              >
                Anuluj
              </Button>
              <Button
                onClick={() => ytThumbnailFromTranscriptMutation.mutate(ytTranscript)}
                disabled={
                  ytTranscript.trim().length < 10 || ytThumbnailFromTranscriptMutation.isPending
                }
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {ytThumbnailFromTranscriptMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizuję...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" /> Wygeneruj propozycję
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={craftModalOpen} onOpenChange={setCraftModalOpen}>
          <DialogContent className="max-w-2xl bg-[#0a0a0a] border-white/[0.08]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white">
                <Sparkles className="w-4 h-4 text-violet-300" />
                Prompt + AI
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-[11px] text-white/50 mb-1">
                  Twoj pomysl (krotko, po polsku spoko)
                </Label>
                <Textarea
                  value={craftIdea}
                  onChange={(e) => setCraftIdea(e.target.value)}
                  placeholder="np. Mateusz w swiecie Minecraft trzyma laptopa, cinematic, daylight"
                  rows={3}
                  className="bg-white/[0.04] border-white/[0.08] text-white text-[13px] resize-none"
                />
              </div>
              {/* Style-reference image (optional) — crafts the prompt to match its visual style. */}
              <div>
                <Label className="text-[11px] text-white/50 mb-1 block">
                  Przykład stylu (opcjonalnie) — wygeneruję prompt w stylu tej grafiki
                </Label>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  id="craft-ref-file-input"
                  onChange={(e) => {
                    loadCraftRefImage(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                {craftRefUploading ? (
                  <div className="w-full flex items-center justify-center gap-2 px-3 py-4 rounded-lg border border-dashed border-white/[0.15] text-[11px] text-white/55">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Wgrywanie przykładu stylu...</span>
                  </div>
                ) : craftRefImage ? (
                  <div className="relative inline-block">
                    <img
                      src={craftRefImage}
                      alt="Referencja stylu"
                      className="h-24 rounded-lg border border-white/[0.12] object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setCraftRefImage(null)}
                      className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-black/80 border border-white/20 text-white/80 flex items-center justify-center hover:bg-black"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => document.getElementById("craft-ref-file-input")?.click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setCraftRefDragOver(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setCraftRefDragOver(false);
                    }}
                    onDrop={handleCraftRefDrop}
                    className={`w-full flex flex-col items-center justify-center gap-1.5 px-3 py-4 rounded-lg border border-dashed text-[11px] transition-all ${craftRefDragOver ? "border-violet-400/60 bg-violet-400/[0.06] text-violet-200" : "border-white/[0.15] text-white/55 hover:text-white hover:border-white/[0.3]"}`}
                  >
                    <ImagePlus className="w-4 h-4" />
                    <span>
                      {craftRefDragOver
                        ? "Upuść grafikę tutaj"
                        : "Przeciągnij, wklej (Ctrl+V) lub wybierz przykład stylu"}
                    </span>
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={!craftIdea.trim() || craftPromptMutation.isPending || craftRefUploading}
                  onClick={() => craftPromptMutation.mutate(craftIdea.trim())}
                  className="bg-gradient-to-br from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white text-xs"
                >
                  {craftPromptMutation.isPending ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                      Generuje...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                      {craftedPrompt ? "Wygeneruj ponownie" : "Wygeneruj prompt"}
                    </>
                  )}
                </Button>
                {craftedPrompt && (
                  <span className="text-[10px] text-white/40">{craftedPrompt.length} znakow</span>
                )}
              </div>
              {craftedPrompt && (
                <div>
                  <Label className="text-[11px] text-white/50 mb-1">
                    Prompt (edytuj jesli chcesz)
                  </Label>
                  <Textarea
                    value={craftedPrompt}
                    onChange={(e) => setCraftedPrompt(e.target.value)}
                    rows={6}
                    className="bg-white/[0.04] border-white/[0.08] text-white/85 text-[11px] resize-none"
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCraftModalOpen(false)}
                className="text-white/60"
              >
                Anuluj
              </Button>
              <Button
                size="sm"
                disabled={!craftedPrompt.trim()}
                onClick={() => {
                  const visible = craftedPrompt.trim();
                  // VP-179: payload tylko gdy user nie przerobił streszczenia po swojemu.
                  if (craftedPromptPayload?.summary.trim() === visible)
                    applyStructuredPrompt(visible, craftedPromptPayload.payload);
                  else setPrompt(visible);
                  setOptimizePromptEnabled(false);
                  setCraftModalOpen(false);
                  toast({
                    title: "Prompt wstawiony",
                    description: "Kliknij Generuj zeby zrobic grafike.",
                  });
                }}
                className="bg-white text-black hover:bg-white/90"
              >
                Uzyj tego promptu
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
